// The whole help corpus, as data. One entry per topic; no JSX, no imports — so it can be filtered,
// searched, and (later) handed to Peter the Project Monkey as his retrieval corpus without dragging
// UI along with it.
//
// Fields:
//   id         stable slug (used by `related` links and deep links)
//   title      what shows in the topic list
//   category   grouping in the topic list
//   audience   'all' | 'manager' | 'admin' — see visibleTopics() for what those mean
//   flag       'resources' | 'scheduling' — hide the topic when that feature is switched off
//   routes     paths this topic is the answer for, so Help opens on the right screen's article
//   keywords   extra search terms that aren't in the prose
//   summary    one line, shown under the title in lists
//   body       markdown-lite: `## heading`, `- bullet`, `1.` list, blank-line paragraphs, **bold**,
//              `code`. That is the whole set HelpDrawer's inline() knows -- a [link](url) and
//              *single-asterisk* emphasis both render as their literal characters. Cross-reference
//              another topic through `related`, which the drawer renders as a real link.
//
// Keep bodies short. Someone opens this mid-task; they want the answer, not the manual.

export const CATEGORIES = [
  'Getting started',
  'Resourcing',
  'Project management',
  'Project implementation',
  'Administration',
  'How things work',
];

export const HELP_TOPICS = [
  // ---------- Getting started ----------
  {
    id: 'getting-started',
    title: 'Getting started',
    category: 'Getting started',
    audience: 'all',
    routes: [],
    keywords: [
      'sign in', 'login', 'account', 'access', 'navigation', 'menu',
      'signed out', 'timed out', 'session expired', 'inactivity', 'timeout',
    ],
    summary: 'Signing in, what you can see, and how the app is laid out.',
    body: `
Sign in with your Aumentum work account — the same credentials as Outlook and Teams. Your user
record is created the first time you sign in; there is nothing to request.

## Finding your way around
- **Left nav** — grouped by stage: Resourcing, Project Management, Project Implementation, Go-Live.
- **Top right** — the notification bell, your name, Sign out, and (for administrators) a
  **Maintenance** link that leads to the admin screens.
- **Help** — the **?** next to your name. It opens on help for whatever screen you're on, and you can
  search from there.

## What you can see
Everyone can open every page in the left nav. What you can *change* depends on you: your own
timesheet always, your team's forecast if you manage people or run a project, and the admin screens
only if you're an administrator. Where a page is read-only for you, it says so rather than failing
on save.

## Staying signed in
Leave the app untouched for two hours and it signs you out. You get a **Still there?** warning with a
minute's notice first — click **Stay signed in** and nothing happens. If you missed it, the sign-in
screen tells you that's what happened; sign back in and carry on.

Two hours of *inactivity*, not two hours of use: a normal working day never triggers it, and if you
have the app open in two tabs, working in either one keeps both alive. Anything you've typed but not
saved is lost, so this is one more reason to save as you go.

If sign-in fails or you're told your account is inactive, contact Jeremy Bromm.
`,
  },
  {
    id: 'notifications',
    title: 'Notifications',
    category: 'Getting started',
    audience: 'all',
    routes: [],
    keywords: ['bell', 'alert', 'unread', 'read', 'badge', 'mark all read'],
    summary: 'The bell in the top right: what raises one, and how read and unread work.',
    body: `
The **bell** in the top right carries a red count when something is waiting for you. Click it for the
list, then click an item to go straight to whatever it's about.

## What raises one
- Someone posts or edits a suggestion (administrators), or replies to one you submitted or follow.
- An administrator changes the status of a suggestion you submitted or follow, or merges yours into
  another.
- A timesheet is submitted to you for approval.

Your own actions never notify you.

## Read and unread
The panel lists **unread** notifications only. Opening one marks it read, so the list empties as you
work through it.
- **Show read** in the panel header brings the older ones back into the list; **Hide read** puts them
  away again. The app remembers which you chose.
- **Mark all read** clears the count in one go, without opening anything.

The bell keeps the 30 most recent — it's a to-do list, not an archive.
`,
    related: ['approvals', 'getting-started'],
  },
  {
    id: 'suggestions-roadmap',
    title: 'Suggestions & Roadmap',
    category: 'Getting started',
    audience: 'all',
    routes: ['/suggestions'],
    keywords: [
      'suggest', 'suggestion', 'idea', 'bug', 'request', 'feedback', 'roadmap', 'changelog',
      'vote', 'me too', 'follow', 'reply', 'delivered', 'whats new',
    ],
    summary: 'Send an idea or bug, see what is planned and shipped, vote, reply and follow.',
    body: `
Click **Suggest** in the top bar to send an idea, a request or a bug. Pick a category, say what you
want or what is broken, and submit. Pulse notes which screen you were on, which helps with bugs.

## The Suggestions & Roadmap page
Open it from **Review Suggestions** in the popup, or from a notification. Everyone sees every
suggestion — that's the point: check before you send, and add your voice to an existing one instead.
- **Roadmap** — a board: **Inbox** (new and under review), **Planned**, **In progress** and
  **Delivered** (last 90 days). Click a card for the full thread.
- **List** — every suggestion as a card, sortable by newest, oldest, most votes or priority.
- **Changelog** — what has shipped, by month, with what changed and who asked for it.

The numbers at the top show what is open, open bugs, what's in progress, what shipped this month
and the typical days from suggestion to delivery.

## Filters
Status, Category, Area and Priority are multi-select — tick as many as you like. The List opens hiding
**Done**, **Declined** and **Duplicate**; tick them under Status to see closed work, or
**Reset filters** to go back. **My suggestions** shows only yours. **Group by area** (List tab) puts
suggestions under the part of Pulse they are about — Project Budgets, Time Reports, Registers and so
on — so related ideas sit together.

## Areas
Each suggestion carries an **area** tag: the screen or feature it is about. It is filled in
automatically, and an administrator can correct it.

## Vote, reply, follow
- **Thumbs-up** — "me too". One per person; click again to take it back. Most votes is how we
  decide what matters.
- **Discussion** — reply in the thread. Edit or delete your own replies.
- **Follow** — get notified of replies and status changes. Replying follows automatically, and you're
  always notified about your own suggestions.

## Editing your own suggestion
Use **Edit** to fix wording or the category while it is **New** or **Under review**. Once it's
**Planned** or beyond, it locks so the replies still make sense — add a reply instead. Edited
suggestions are tagged **edited**.

## Statuses
**New** → **Under review** → **Planned** → **In progress** → **Done**. **Declined** keeps a "no" and
the reason visible. **Duplicate** means it was merged into an existing suggestion; your vote and
follow moved with it.
`,
    related: ['notifications'],
  },
  {
    id: 'suggestions-admin',
    title: 'Running the roadmap (administrators)',
    category: 'Administration',
    audience: 'admin',
    routes: [],
    keywords: [
      'triage', 'status', 'delivered', 'release note', 'merge', 'duplicate', 'priority',
      'target release', 'ai triage', 'deliver',
    ],
    summary: 'Move suggestions through the roadmap, record delivery, merge duplicates.',
    body: `
Administrators get a **status dropdown** on every card on **Suggestions & Roadmap**. Picking a status
saves at once and notifies the submitter and followers. Open **Roadmap & delivery** on a card for the
rest.

## Roadmap & delivery
- **Priority** and **Target release** (free text, such as "Oct 2026") show on the card and the board.
- **Delivered on / Delivered by** — filled in with today and you the first time a card moves to
  **Done**; change either afterwards if it shipped earlier or someone else built it.
- **Release note** — one plain sentence on what changed. It is the headline on the **Changelog**.
- **Link** — the pull request, decision or document.

Moving to **Done** opens this panel for you so the note and link don't get skipped.

## Duplicates
Choose **Mark as duplicate of…** and **Merge**. The suggestion becomes **Duplicate**; its submitter's
vote and follow move to the one you merged into, and they're told. Replies stay where they were.

## AI triage
New suggestions get a one-line summary, a suggested category, an **area**, a possible duplicate and up
to three **related** suggestions about the same screen or feature (handy for planning them together),
shown at the top of **Roadmap & delivery**. Only administrators see the hints. The area is filled in
only when empty and never overwrites one you set; everything else is applied only if you click. If
suggestions have no triage or area yet, a banner on the page offers **Run AI triage on them**.

## Closing items from the command line
Developers can record shipped work without the page: the **/deliver** skill in Claude Code matches
the current branch to an open suggestion, drafts the note and marks it Done after you confirm.
`,
    related: ['suggestions-roadmap'],
  },
  {
    id: 'view-as-customer',
    title: 'Viewing the Hub as a customer',
    category: 'Getting started',
    audience: 'all',
    routes: [],
    keywords: ['view as customer', 'customer view', 'customer portal', 'preview', 'external', 'view as self'],
    summary: 'Check what a customer contact actually sees on a project, then switch back.',
    body: `
Click **your name** in the top right and choose a project under **View as Customer** — or **All
projects** — and the Hub reloads as the customer portal: the customer's own navigation, their
screens, their data, read-only.

**View as Self**, in the same menu, puts you back. So does signing out, however that happens.
While you're in the customer view a yellow **Customer view** badge sits next to your name.

## What you'll see
- Only the customer-facing screens (Project Schedule, Project Links, Project Registers, Business
  Process Analysis) — no timesheets, no forecasting, no Configuration.
- Only what a contact on that project is allowed to see. Business Process Analysis holds its
  internal-only columns back, exactly as it does for a real customer.
- Nothing you can edit. It's the same restriction a customer has, not a display setting.

## Worth knowing
- The list offers **fixed-fee (POC)** projects, archived ones excluded — the same rule as the
  Project Registers and BPA pickers.
- You don't need a customer contact to exist for the project. This is your own account being
  shown less, not you signing in as somebody else, and it's recorded as your own session.
- Customers never see this menu.
`,
    related: ['roles-and-permissions', 'project-registers', 'bpa'],
  },
  {
    id: 'working-in-grids',
    title: 'Working in the grids',
    category: 'Getting started',
    audience: 'all',
    routes: [],
    keywords: ['sort', 'save', 'edit', 'grid', 'table', 'export', 'excel', 'print', 'expand', 'collapse'],
    summary: 'Conventions every grid in the app shares: sorting, saving, expanding, exporting.',
    body: `
Most screens here are grids, and they behave the same way throughout.

- **Click a column header to sort.** Almost every grid does this. The exceptions are grids that mirror
  an external format — the Accounting export and the month columns on the forecast — where sorting
  would break the layout Finance or the calendar depends on.
- **Two save styles.** Admin and forecast grids collect your edits and write them on one **Save changes**
  at the top; My Time saves each cell as you leave it, with no Save button. Nothing in the app saves a
  half-typed cell you're still in.
- **A failed row stays dirty.** When a batch save partly fails, the rows that saved go clean and the
  ones that didn't stay editable for a retry — you don't lose the rest of the batch.
- **+ / − and ▸ expand.** Roll-up grids open one level at a time: panel → project → team → person.
- **Export where it exists.** The Accounting and FCST vs Actuals reports have **Export to Excel** and
  **Print / Save as PDF**; printing hides the nav and buttons so the page prints as laid out.
`,
    related: ['my-time', 'time-reports', 'resource-configuration'],
  },
  {
    id: 'roles-and-permissions',
    title: 'Who can do what',
    category: 'How things work',
    audience: 'all',
    routes: [],
    keywords: ['role', 'admin', 'administrator', 'viewer', 'manager', 'permission', 'pm', 'project manager', 'access denied', 'new user', 'new hire', 'read-only'],
    summary: 'Viewer, member, manager, project manager, administrator — and what each unlocks.',
    body: `
There are three assigned roles — **viewer**, **member**, **administrator** — plus two things that
are worked out from the data rather than assigned.

- **Viewer** — the default for every new sign-in. Read-only: can see every screen and report, but
  every write (timesheet entry, edits, anything that isn't a GET) is blocked, no matter what else
  their row says. An administrator promotes them once their access is confirmed.
- **Member** — log your own time, submit your weeks, read every report and forecast, edit what
  you're allowed to edit.
- **Manager** — anyone listed as someone else's manager. Approves their timesheets, can enter time on
  their behalf, and can edit forecast hours.
- **Project manager** — the PM on a project. Approves time logged *to that project*, and can edit its
  forecast.
- **Administrator** — the above plus the configuration screens: people, teams, timesheet tasks, the
  fiscal calendar, projects and accounts, project templates, maintenance mode, login history,
  customer access.

You become a manager by being set as someone's manager, and a PM by being set as a project's PM —
both on the admin configuration screens. **New hire checklist:** get them assigned to the app in
Entra first (that's a separate, earlier gate — ask an admin with directory access), then once they've
signed in once, go to Configuration → People and change their Role from **Viewer** to **Member** (or
**Admin**). Ask an administrator if your own access looks wrong.
`,
  },

  // ---------- Resourcing ----------
  {
    id: 'my-time',
    title: 'My Time (timesheet)',
    category: 'Resourcing',
    audience: 'all',
    flag: 'resources',
    routes: ['/my-time'],
    keywords: ['timesheet', 'hours', 'log time', 'submit', 'week', 'comment', 'note', 'utilization'],
    summary: 'Log hours for the week, add the projects you work on, and submit.',
    body: `
The timesheet week runs **Saturday to Friday**. Use **← Prev week / Next week → / This week** to move
around; the small line above the dates tells you which fiscal month the week reports into, and warns
you when a week straddles two of them.

## Logging hours
1. Pick a project from **+ Add a project…**, choose the task, then **+ Add row** — that row now stays
   on your timesheet week to week.
2. Type hours into the day cells.
3. **There is no Save button.** A cell saves when you leave it (tab or click away); it flashes while
   saving. **Enter works like Tab** — it saves the cell and moves to the next one.

**Starting the week from last week:** click **Copy last week** to fill this week's empty cells with the
same hours, on the same rows and weekdays, as last week. It never overwrites hours you've already
entered, and it skips holidays and closed (archived) days, so it's safe to click twice. Adjust anything
that differs. The button is disabled once the week is submitted.

The **✕** at the end of a row removes it from your timesheet. That's a display change only — hours you
already logged stay exactly where they are, and re-adding the row shows them again. **Once a row has
approved hours, the ✕ is disabled** — removing it would hide those hours from view without touching
the approval sitting on them. Rows are grouped by customer/project, with the project's total for the
week under its name.

## Non-project time
Time that isn't project work — PTO, internal meetings, and so on — lives on the **Administrative Time**
project; add it the same way. Two of those tasks (**Travel** and **Post Go Live Support**) are done *for a
customer*, so they ask for a **customer** instead of a project, and the customer becomes the row's
headline. Travel for two customers is two separate rows.

## Comments
Some tasks require a note saying what the time was spent on. Click the bubble in the **Notes** column
to add one — it covers the whole week for that row. If a required comment is missing, the row shows
*Comment required* and the week won't submit until every one is filled in (the whole week is
submitted in one go, so one missing note blocks all of it).

## Submitting
**Submit week** hands the week to your approver — but only what's actually there. You don't need
every row filled in first; submit a partial week whenever you like, then keep adding to it.

Submission tracks each row, not the whole week: add hours after a week is already submitted or
approved, and **Submit week** shows up again — it sends only the newly-added hours for approval,
leaving already-submitted or -approved rows exactly as they are.

**Unsubmit week** pulls back whatever's still submitted-but-not-yet-approved, back to an editable
draft — comment included. It only appears while there's something in that state; once a row is
approved, the button has nothing left to act on for it.

**If a row has already been approved, you can't unsubmit it yourself.** Ask your manager (or the
project's PM, whichever approved it) to **unapprove** it from their Approvals queue — see
*Approving timesheets*. Unapproving doesn't unsubmit the row; it just drops it back into that
approver's pending queue, still locked. **Unsubmit week** reappears on your timesheet once it's back
to submitted-not-approved, so you can pull it back to draft yourself from there.

If a fiscal period has been archived, its days are grayed out and closed to entry — that overrides
everything above, since archiving closes the day to any change, approved or not. Ask an
administrator if you need one reopened.

## The two summary cards
- **Month — Billable** — FCST (forecast), Logged and Remaining per project for the fiscal month.
  Remaining goes red when you're over the forecast.
- **This Week Recap** — hours by billable category, plus billable and productive utilization for the
  week. Both percentages exclude time off from the total.
`,
    related: ['billable-categories', 'time-off', 'fiscal-calendar', 'approvals', 'proxy-time-entry',
      'administrative-time', 'working-in-grids'],
  },
  {
    id: 'proxy-time-entry',
    title: 'Entering time for someone else',
    category: 'Resourcing',
    audience: 'manager',
    flag: 'resources',
    routes: [],
    keywords: ['proxy', 'on behalf', 'someone else', 'my team'],
    summary: 'Managers can fill in a team member\'s timesheet for them.',
    body: `
If you manage people, **My Time** shows an **Entering time for** dropdown at the top. Pick a person
and the grid becomes their timesheet — a banner makes it obvious, and hours save to their week, not
yours. Switch back with **Myself**.

Use it for someone on leave or offline, not as the normal route: they can't see that you're mid-edit,
and you're the one submitting on their behalf.

The same dropdown does a second job: on the **Approvals** panel it reads **Approving as** and hands
you that person's approval queue instead of their timesheet — see the **Approving timesheets** topic.
`,
    related: ['my-time', 'approvals'],
  },
  {
    id: 'approvals',
    title: 'Approving timesheets',
    category: 'Resourcing',
    audience: 'manager',
    flag: 'resources',
    routes: [],
    keywords: ['approve', 'approval', 'reject', 'pending', 'queue', 'submitted'],
    summary: 'Where the approval queue lives and what you\'re approving.',
    body: `
Approvals sit **on the My Time page**, not on Resource Planning — next to your own timesheet, since
it's the same weekly rhythm. If anything is waiting on you, an **Approvals (n)** button appears at
the top; **← Back to my timesheet** returns.

The queue opens on the week of the oldest entry still owed, so working forward clears the backlog in
order. You'll see up to two sections:

- **As manager** — time your direct reports submitted, whichever project it was logged to.
- **As project manager** — time anyone logged to a project you run.

**Approval has two gates**, and they're separate: the manager's, then the project manager's. Time you
approve as a manager still shows as *Manager Approved* until the PM signs off too — so a week sitting
at that status is waiting on the project, not on you.

The grid mirrors My Time's layout (person → project → task) and shows each row's comment, so you're
approving hours and their explanation together. Only **submitted** time appears — an unsubmitted week
isn't waiting on you.

## Undoing an approval
A **Pending / Approved** toggle sits above the grid. Switch to **Approved** to see what you've
already signed off, select rows, and either:

- **Unapprove selected** — drops the rows back into your **Pending** queue, still locked, for a
  fresh look. Nothing else about them changes.
- **Reject selected…** — the same required-reason flow as rejecting from Pending, now usable on
  already-approved rows too. It clears **both** approvals and unsubmits the row, handing it back to
  the owner as an editable draft.

The two gates unwind in the reverse of the order they're applied (PM approves after the manager, so
PM approval never outlives the manager's): if the PM has already signed off, **Unapprove** on the
manager side is blocked — the row stays selectable but the button shows *"the PM must unapprove
first"* rather than doing nothing. **Reject** has no such restriction, since it resets both sides at
once — use it when you need to fully undo a row rather than just re-open your own queue for it.

Unapproving doesn't hand the row back to the timesheet owner to edit — it's still locked, just
sitting in your pending queue instead of approved. If the owner actually needs to change it, either
reject it (above) or unsubmit it themselves from **My Time** once it's back to submitted-not-approved
(see *My Time (timesheet)*).

## Approving for another manager or PM
The dropdown at the top of the page — **Entering time for** on your timesheet — reads **Approving as**
here, and switches you into someone else's queue: their reports, their projects, their oldest
outstanding week. Use it when an approver is on leave and their team's time is stuck.

- You can stand in for **anyone below you in the reporting chain**, at any depth, not just your direct
  reports. Administrators can stand in for anyone.
- You get the queues **that person** has. Pick someone who runs projects but manages nobody and only **As
  project manager** appears; pick someone who is neither and the page says so.
- Standing in for someone **narrows what you can approve** to their reports and projects — including
  for administrators. Switch back to **Myself** for your own reach.
- The sign-off is still recorded as yours. There's no "approved on behalf of" — the approval history
  names whoever clicked.
`,
    related: ['my-time', 'proxy-time-entry', 'timesheet-compliance', 'roles-and-permissions'],
  },
  {
    id: 'resource-planning',
    title: 'Resource Planning',
    category: 'Resourcing',
    audience: 'all',
    flag: 'resources',
    routes: ['/resource-planning'],
    keywords: ['forecast', 'fcst', 'allocation', 'baseline', 'capacity', 'utilization', 'plan'],
    summary: 'Forecast hours by project and person, compare against baselines, check capacity.',
    body: `
Four views, from the tabs at the top.

## Forecasting
Forecast (**FCST**) hours per person, per project, per calendar month for the selected year. Rows are
grouped **Project → Team → Person** and start collapsed at project level — expand to see who the
project's hours belong to.

- Type into a month cell to change the plan; roll-ups and the footer update as you type.
- **Save** writes every changed row in one go. Nothing is saved until you press it.
- Editing is for managers, PMs and administrators. Everyone else sees the same numbers, read-only.
- The **current month and every month before it are closed** — a 🔒 in the column header, cells you
  can't type into. Administrators can reopen a month by clicking its padlock.
- Add a person to a project with the picker on the project's row. Archived projects and merged
  placeholder projects aren't offered — a finished project takes no new forecast.

## Baselines
The locked plan you're being measured against: an **Annual** baseline plus optional **Q1–Q4** re-locks.
Baselines are deliberately **read-only** — one you can edit isn't a baseline. Each figure shows the
variance between what was committed and what's now forecast, at project and team level. A team the
baseline committed to but the current forecast doesn't mention still appears, so a gap can't hide.

## Capacity
Hours, not percentages: how many hours are **allocated** to each team, person and project, and how many
are still **open**, over a window of 12, 18 or 24 months. It also holds the **Budget** compare and, for
people who can edit forecasts, a **Forecast** tab for changing team-level hours in place. See
*Capacity, in detail*.

## Utilization
The same forecast as a percentage of each person's capacity, color-banded, with **What-If** scenarios
(slip, stall or drop a project). See *Utilization, in detail*.
`,
    related: ['capacity-math', 'fcst-vs-actuals', 'resource-configuration', 'capacity-view', 'utilization-view'],
  },
  {
    id: 'team-allocations',
    title: 'Forecasting, in detail',
    category: 'Resourcing',
    audience: 'all',
    flag: 'resources',
    parent: 'resource-planning',
    routes: [],
    keywords: ['allocation', 'fcst', 'forecast hours', 'assign', 'add person', 'team plan', 'team level', 'unassigned hours', 'filter', 'save', 'locked month', 'closed month', 'unlock'],
    summary: 'Adding people to a project, filling in months, and what the roll-up rows mean.',
    body: `
The grid is one row per person per project, twelve month columns for the chosen year.

## Putting hours on the plan
- Expand a project to its teams and people, then type into the month cells. Tab or arrow between
  cells; the up/down arrows walk the column rather than nudging the number.
- Project and team rows are **roll-ups** — they total the people under them and can't be typed into.
  They update live as you type, so you can watch a project's month total while editing one person.
- **Save** writes every changed row at once. Navigating away first loses the edits.

## Planning at team level, before anyone is named
When you know the hours a team needs on a project but not who will work them, click **+ Add**, set
**Level** to *A team plan*, and pick the project and team. A project/team row appears in the grid;
type the monthly hours straight into it and **Save All**. The row is only a draft until you save, and
a team row with no hours disappears on reload.

Once people are assigned to that project and team, their hours are the forecast and the team plan
becomes the target they're measured against — the two are never added together. To start someone from
the team's figure, use **+ Add → A person** and choose *Copy the team's forecast*.

## Adding someone
Use **+ Add** with Level set to *A person on a project*. Anyone already on that project **for the chosen year** is still
offered — an assignment carries no year of its own, so someone with 2026 hours needs offering again
to be given 2027 hours.

Not offered: archived projects, and placeholder projects that have already been merged into a real
one (their forecast moved with them).

## Closed months
The current month and everything before it are **closed to edits** — that time has been reported on,
and the current month is part-way through being actualled. A closed column carries a 🔒 beside its
name and its cells can't be typed into, at person or team level. Copying a team's forecast onto a
newly added person fills the open months only, for the same reason.

Administrators can **click the padlock to reopen a month**, which unlocks that one column until you
leave the screen or change the year — it isn't a permanent change, and it applies to you, not to
everyone. Use it to correct a month, not to keep planning in it.

## Filters and sorting
Filter by project, team or person to cut a wide grid down. The three identifying columns sort on
click; the month columns don't — they're a fixed calendar sequence.

If the cells are read-only for you, you're not a manager, PM or admin. The numbers are still the real
ones.
`,
    related: ['resource-planning', 'baselines-view', 'fcst-vs-actuals', 'placeholder-projects'],
  },
  {
    id: 'baselines-view',
    title: 'Baselines, in detail',
    category: 'Resourcing',
    audience: 'all',
    flag: 'resources',
    parent: 'resource-planning',
    routes: [],
    keywords: ['baseline', 'lock', 'annual', 'quarterly', 'variance', 'commitment'],
    summary: 'Reading a locked plan and its variance against today\'s forecast.',
    body: `
A baseline is the plan as it stood when it was locked. **Annual** is the one people mean by "the
baseline"; **Q1–Q4** are re-locks for when a quarter's plan is deliberately reset.

Each figure shows the locked commitment with the variance to today's forecast above it — negative
(red) means the forecast is now below what was committed. Variance is reported at **project and team
level only**: a baseline records a team-level commitment, and pushing it down to individuals would
invent detail the baseline never had.

Two things worth knowing when a number looks odd:

- **A team the baseline committed to but nothing is forecast against still appears**, showing its full
  shortfall — a dropped team can't hide by being absent.
- **Baselines keep people who have since left or been renamed**, because a baseline that reads
  differently after an HR change isn't a record of anything.

The view is read-only by design. To change what you're measured against, lock a new baseline.
`,
    related: ['resource-planning', 'fcst-vs-actuals', 'team-allocations'],
  },
  {
    id: 'capacity-view',
    title: 'Capacity, in detail',
    category: 'Resourcing',
    audience: 'all',
    flag: 'resources',
    parent: 'resource-planning',
    routes: [],
    keywords: ['capacity', 'hours allocated', 'hours remaining', 'open hours', 'budget', 'forecast', 'overbooked', 'pipeline', 'planner'],
    summary: 'Hours allocated and hours still open, by team, person and project, plus budget and forecast editing.',
    body: `
The **Capacity** tab speaks in hours. Percentages live on the Utilization tab.

## The toolbar
Teams (pick several), a saved **Scenario** from the Utilization tab's What-If, **Month / Quarter / Year**
grouping, **Include pipeline** (on by default — pipeline projects count as demand), a **Timeline** of 12, 18
or 24 months, and ◀ Today ▶ to move the window.

## Hours allocated and hours remaining
Overview and By Resource have a toggle. **Hours allocated** is forecast hours. **Hours remaining** is
capacity minus allocated: green is open, amber is thin (under 16 hours a month), red with a minus sign is
overbooked.

## Overview
Totals for the window, a monthly chart and a team table. Each bar is shaded by utilization with the same
bands as the Utilization tab (65%+ green, 50–64% yellow, under 50% red, over 100% purple), carries its
utilization % above it, and the **dashed line marks 65% of capacity**. If anyone has no capacity set
(no standard monthly hours, or outside their employment dates) they're left out of every number and a
banner names them.

## By Resource and By Project
Both are collapsible, starting collapsed, with **Expand all** and **Collapse all**. By Resource shows each
team and, expanded, its people. A **Not yet assigned** row is team-level forecast nobody is named on yet;
it's already counted in the team's number. By Project shows each project's hours and, expanded, which
teams carry them.

## Budget
Per project and team: budget hours, hours logged so far, forecast from this month on, and **budget left
after forecast**. Where no budget hours have been entered the row says **Not set** — nothing is estimated.
Budget hours are still being filled in project by project.

## Forecast (managers, PMs and admins)
Edit the **team-level forecast**: hours per project, per team, per month. Type into a cell; changed cells
are highlighted and **Save** writes only those, to the same forecast the Forecasting tab uses. Beneath the
grid, each team's **open hours** update as you type, and each project's **budget left** does too. Add a
team to a project with the pickers above the grid.

Read-only cells: months where hours are named to **people** (those people are the forecast — change them
in Forecasting), and the current month and earlier. Switch the scenario back to Baseline first.

This reads **forecast**, not logged time.
`,
    related: ['utilization-view', 'team-allocations', 'capacity-math', 'resource-planning', 'config-people'],
  },
  {
    id: 'utilization-view',
    title: 'Utilization, in detail',
    category: 'Resourcing',
    audience: 'all',
    flag: 'resources',
    parent: 'resource-planning',
    routes: [],
    keywords: ['utilization', 'what-if', 'scenario', 'slip', 'stall', 'drop', 'percent', 'bands', 'by resource', 'by project'],
    summary: 'Forecast as a percent of capacity, with what-if scenarios.',
    body: `
**Utilization = assigned hours ÷ the person's own capacity**, so a half-time person at half-time hours is
100%. Team and quarter figures add the hours first, so a half-time person counts half as much.

**Bands:** 65% and up green, 50–64% yellow, under 50% red ▼, over 100% purple ▲. The band is chosen from
the rounded percent. Low utilization is red because it's unused capacity.

## Overview
KPI tiles, a monthly chart with each bar's %, a team table with an **All teams** row, **who needs
attention** (people under 50% or over 100% over the next six months) and **horizon alerts**.

## By Resource and By Project
Teams are collapsed; open one with its arrow, or use Expand all and Collapse all. **Not yet assigned** is
team-level forecast not yet named to a person. By Project shows each project's hours as a share of the
team's capacity (shaded, not banded — 20% on one project isn't "low").

## What-If
Set any project to **On plan**, **Slips 3 months**, **Stalls 3 months** or **Drops out**. Slip moves the
whole project; stall keeps work already behind us and pushes the rest. The baseline is never changed and a
banner shows when a scenario is on. **Save scenario…** keeps it under a name (yours only); pick it later
from the Scenario list, which the Capacity tab also reads.
`,
    related: ['capacity-view', 'capacity-math', 'resource-planning'],
  },
  {
    id: 'time-reports',
    title: 'Time Reports',
    category: 'Resourcing',
    audience: 'all',
    flag: 'resources',
    routes: ['/time-reports'],
    keywords: ['report', 'compliance', 'accounting', 'export', 'hours by category', 'fcst vs actuals', 'variance', 'sightline', 'r&d', 'year over year', 'yoy', 'utilization'],
    summary: 'Six reports over logged time: compliance, the Finance export, hours by category, plan vs actual, Sightline Feed, year over year.',
    body: `
Pick a **fiscal year** at the top — every period selector below it narrows to that year. Filters
cascade: choosing a team narrows the manager list, and choosing a manager narrows the people list.

## Compliance
Who has submitted and who hasn't, for the chosen fiscal period. A person shows the worst status they
have in any week of the period — the question being answered is "who still needs chasing". Sort by any
column; default is by team.

## Accounting
The export Finance works from, in their own layout: rows grouped by project with subtotals on the
project row. This is the one grid with no click-to-sort — sorting would break the grouping their
format depends on.

## Hours by Category
Hours rolled up into panels (billable project work, core product work, and so on). **+ / −** in a
panel's title steps one level at a time: panel → project → team → person. This is the only report that
can combine several fiscal periods at once — use the period dropdown's checkboxes.

## FCST vs Actuals
Forecast against logged hours per project, as two bars on a shared scale. Variance is signed:
**positive means more hours burned than planned**. Green is over forecast, red is under — the risk this
report watches for is work that was forecast and never delivered. *Achievement* is how much of the plan
was delivered; *Accuracy* is how close the plan was in either direction. Both are blank when there was
no plan to measure against — an unplanned project is unmeasured, not 0%.

Projects on internal accounts are excluded throughout. Archived projects stay: their history still
counts. Tick **Include R&D** to add each project's R&D hours (from **R&D Time Entry**) as an extra
row; it's off by default, and a team, manager or person filter drops it.

## Sightline Feed
Total hours per project, split **PS** and **R&D**, for keying into Sightline by hand. Projects list
POC first, then T&M, then B&B. Use the **▲ / ▼** beside a project to match Sightline's row order
(saved on your computer). **Export to Excel** follows that order.

## ACT Year over Year
Actual hours for one calendar year against another, month by month. Pick **Year** and **Compare to**;
the month range defaults to January through the last **closed** month, so a half-finished month isn't
set against a full one (an open month in the range shows a warning).

- **Tiles:** ACT, billable, utilization, productive utilization, non-billable productive, non-billable
  and time off, each with last year beside it.
- **Charts:** monthly bars stacked by category (hours or % of capacity), and utilization lines for both
  years. Click a month to drill in.
- **Detail grid:** up to three row levels (team, person, account, project, task type and more), by
  month, with a heatmap and search. Click a row for the breakdown panel; **Export to Excel** takes
  whatever is filtered.
- **Filters** (all combine): team, person, account, project, project type, category, task type, billed-to
  and **Approved only**.

Utilization shows only where it has a denominator — by team or person. It uses the same capacity as
Hours by Category, and only the team and person filters change it; narrowing to one project reads as
"share of capacity spent on this project".

**Mind the years.** Time before FY25 came from the OnePlan import and covers project work only, so
non-billable, travel and time-off hours look near zero and a ratio against total hours means little.
Billable comparisons are only trustworthy from FY24. **Approved only** is close to empty before
FY25-M08, when approval practice changed. Ask Judy or Jeremy for the timesheet data-quality write-up if you need the detail.

## How far back the data goes
Logged time runs from **July 2025** to today, with a handful of earlier entries from December 2024.
Everything before people started entering time in this app was imported from OnePlan, day by day and
against the same task types you pick from now — so a report covering last autumn breaks down the same
way as one covering last week. Earlier months used to arrive as a single lump per person per project;
that's no longer the case, and any report you ran before 2026-08-25 will look different for those
months.

Where a day existed in both systems during the changeover, **what someone entered here won** — this
app is the system of record. A period with visibly light hours is more likely time that was never
logged than time that was lost.
`,
    related: ['fiscal-calendar', 'billable-categories', 'resource-planning'],
  },
  {
    id: 'compliance-report',
    title: 'Compliance report, in detail',
    category: 'Resourcing',
    audience: 'all',
    flag: 'resources',
    parent: 'time-reports',
    routes: [],
    keywords: ['compliance', 'chase', 'missing timesheets', 'not started', 'filter'],
    summary: 'Chasing unsubmitted time, week by week.',
    body: `
One row per person, one column per fiscal week in the period, plus their overall status for the period.

Filter by **team**, then **manager**, then **person** — each choice narrows the next, so picking a team
leaves only that team's managers to choose from. Default sort is by team; every column sorts on click.

Read it as a chase list. A person's period status is the **worst** week they have, so someone showing
*In Progress* may have three clean weeks and one they never submitted — the week columns tell you
which.

Statuses are explained in *What "compliance" means on a timesheet*.
`,
    related: ['timesheet-compliance', 'time-reports', 'approvals'],
  },
  {
    id: 'accounting-export',
    title: 'Accounting export, in detail',
    category: 'Resourcing',
    audience: 'all',
    flag: 'resources',
    parent: 'time-reports',
    routes: [],
    keywords: ['accounting', 'finance', 'export', 'excel', 'wip', 'month end', 'invoice'],
    summary: 'The month-end hours hand-off to Finance.',
    body: `
Hours for one fiscal month in the layout Finance works from: rows grouped by project, with each
project's subtotal on the project row, Excel-pivot style.

- **Export to Excel** produces the file to send on; **Print / Save as PDF** prints it as laid out.
- **No click-to-sort here.** It's the one deliberate exception — this grid mirrors Finance's own export
  format, and sorting would break the grouping that format depends on.
- Projects on internal accounts are excluded, matching what the report itself is for.

Run it after the month's timesheets are in. The **Compliance** tab is how you check that before
exporting.
`,
    related: ['compliance-report', 'time-reports', 'fiscal-calendar'],
  },
  {
    id: 'hours-by-category',
    title: 'Hours by Category, in detail',
    category: 'Resourcing',
    audience: 'all',
    flag: 'resources',
    parent: 'time-reports',
    routes: [],
    keywords: ['hours by category', 'panels', 'billable project work', 'core product', 'market', 'multi period', 'utilization', 'week filter', 'travel'],
    summary: 'Where the hours went, by the bucket the work is billed to — plus utilization against capacity.',
    body: `
Panels split hours by **which budget the work lands in** — billable project work, market work, core
product work, administrative — which is a different question from whether the time was billable. A
non-billable hour on a customer project and a non-billable hour on internal admin sit in different
panels.

- **+ / −** in a panel's title steps one level: panel → project → team → person.
- This is the only report that can span **several fiscal periods at once** — tick them in the period
  dropdown. Useful for a quarter or a whole fiscal year in one read.
- **Week(s)** narrows further, to individual fiscal weeks inside the periods you've picked. Leave it
  on "All weeks" for the whole period.
- Team, manager and person filters cascade the same way as Compliance.

### The summary tiles

- **Total** is every hour in the report, time off included — so it deliberately won't tie to the three
  category tiles, which exclude it. The time-off share is named underneath.
- **Billable**, **Non-Billable Productive** and **Non-Billable** each list the cards that feed them in
  small text. That matters because the mapping isn't one card to one tile: **Travel by Customer**
  splits across two, counting as productive time for travel to a customer and non-billable for
  internal travel.

### The two utilization rates

- **Billable Utilization** — billable hours ÷ capacity.
- **Productive Utilization** — billable + non-billable productive hours ÷ capacity. Purely
  non-billable time is in neither; time off is excluded from both.

The formula, including the capacity figure and how many people it covers, is printed under each tile.

The denominator is **capacity**, not hours logged — which is the one difference from the rates on your
own timesheet. A quiet month reads low here, where My Time would still read 100% if everything you
logged was billable. Capacity respects each person's start/end dates and any monthly overrides
(see *How capacity is worked out*), and people with no capacity on file are left out of the
denominator and counted as "excluded" rather than dragging it down. Picking part of a period by week
scales capacity to match.
`,
    related: ['billable-categories', 'time-reports', 'administrative-time', 'capacity-math', 'config-people'],
  },
  {
    id: 'fcst-vs-actuals-report',
    title: 'FCST vs Actuals, in detail',
    category: 'Resourcing',
    audience: 'all',
    flag: 'resources',
    parent: 'time-reports',
    routes: [],
    keywords: ['fcst vs actuals', 'variance', 'achievement', 'accuracy', 'burn', 'plan vs actual'],
    summary: 'Reading the plan-versus-actual bars, and the two percentages.',
    body: `
Two bars per project on a shared scale — forecast and logged — so bar lengths are comparable down the
whole list, not just within a row.

- **Variance** is signed: **positive = more hours burned than planned.** Green is over forecast, red is
  under, because the risk being watched here is work that was forecast and never delivered.
- **Achievement** — how much of the plan was actually delivered.
- **Accuracy** — how close the plan was in *either* direction, so a 20% over-run and a 20% under-run
  score the same.
- Both percentages are blank where there was no plan: an unplanned project is unmeasured, not 0%.

Expand a project to its teams, then to people. **Export to Excel** and **Print / Save as PDF** are both
here. Definitions are first-pass and don't reproduce the old PowerBI report's own maths — worth a sanity
check before quoting them outside the team.
`,
    related: ['fcst-vs-actuals', 'time-reports', 'baselines-view'],
  },
  {
    id: 'sightline-feed-report',
    title: 'Sightline Feed, in detail',
    category: 'Resourcing',
    audience: 'all',
    flag: 'resources',
    parent: 'time-reports',
    routes: [],
    keywords: ['sightline', 'feed', 'ps hours', 'r&d hours', 'export', 'poc', 'b&b', 'reorder'],
    summary: 'Per-project PS and R&D hours laid out for hand entry into Sightline.',
    body: `
Pick a fiscal period (and optionally projects or project types), then **Run Report**. Each project
gets one line: **PS hrs**, **R&D hrs** and their total, grouped POC → T&M → B&B with a subtotal per
group.

- **PS hours** are billable delivery from timesheets — same rule as FCST vs Actuals, so Internal-account
  projects, Market, Core Product and Administrative time are out.
- **R&D hours** are the actuals entered in **R&D Time Entry**.
- Projects with no hours in the selected periods are left off.
- **▲ / ▼** moves a project within its group, to mirror Sightline's own order. The order is saved in
  your browser, so each person keeps their own.
- **Export to Excel** writes one row per project (period, type, project, manager, PS, R&D, total) in
  the on-screen order — the file an import into Sightline can later read.
`,
    related: ['time-reports', 'rnd-time-entry', 'fcst-vs-actuals-report'],
  },
  {
    id: 'rnd-time-entry',
    title: 'R&D Time Entry',
    category: 'Resourcing',
    audience: 'all',
    flag: 'resources',
    routes: ['/rnd-time'],
    keywords: ['r&d', 'research', 'forecast', 'actuals', 'monthly', 'hours'],
    summary: 'Monthly R&D forecast and actual hours per project — no timesheets.',
    body: `
One forecast and one actual number per project per month. There are no timesheets for R&D, and these
hours stay out of PS forecasts, actuals and capacity unless a report is asked to include them.

Where they show up: **FCST vs Actuals** (tick **Include R&D**) and **Sightline Feed** (always, in its
own R&D column). Not in the side menu — open it from FCST vs Actuals' **(enter)** link or from
Resource Planning › Forecasting.

**Who can edit:** administrators, managers, project managers, and anyone given the **R&D** role by an
administrator. Everyone else sees the numbers read-only.
`,
    related: ['time-reports', 'sightline-feed-report', 'fcst-vs-actuals-report'],
  },

  // ---------- Project management ----------
  {
    id: 'dashboard',
    title: 'Project Dashboard',
    category: 'Project management',
    audience: 'all',
    routes: ['/'],
    keywords: ['dashboard', 'home', 'health', 'rag', 'blockers', 'portfolio'],
    summary: 'Concept preview — sample data showing the intended portfolio view.',
    body: `
**Nothing on this page is real yet.** Every project, percentage and blocker is sample data,
illustrating the layout the Portfolio Dashboard will use once it's built on this app's own data. The
banner at the top says the same thing.

What it's showing you is the intended shape: counts of on-track / at-risk / critical projects, a row
per project with progress, next milestone and days to go-live, and a click-to-expand panel with
modules in scope and blockers.

For real project health today, use **Portfolio Timeline** — it runs on live schedule data. For hours
and forecast numbers, use **Time Reports** and **Resource Planning**.
`,
    related: ['portfolio-timeline', 'time-reports'],
  },
  {
    id: 'portfolio-timeline',
    title: 'Portfolio Timeline',
    category: 'Project management',
    audience: 'all',
    routes: ['/timeline'],
    keywords: [
      'timeline', 'gantt', 'portfolio', 'leadership', 'go-live', 'golive', 'workstream', 'phase',
      'milestone', 'progress', 'roadmap',
    ],
    summary: 'Every fixed-fee project on one timeline, from its real schedule.',
    body: `
One row per project, drawn from each project's own schedule — the same phases, workstreams and
milestones you'd see on Project Schedule, rolled up to a single bar.

Only **fixed-fee (POC)** engagements appear. T&M letters of authorization are billed hourly and have
no plan to draw, and internal time buckets aren't projects. Tick **Show closed projects** to include
Completed and Archived work.

## Reading a row
- **Go-Live** is the date the bar ends at. A tilde (**~Apr 2027**) means no go-live milestone has been
  flagged, so the date is inferred — hover it to see what from.
- **WENT LIVE** under the date means the go-live milestone has an actual date recorded.
- **Progress** is weighted by task duration where the project has a promoted schedule. An **ms**
  suffix means it's the share of milestones delivered instead, because that project has no task plan.
- The colored dot before the project name is its RAG status; **⚠ 3** after the PM's name counts
  milestones past their target date.
- A **NOT SCHEDULED** pill means the project has no dates at all yet — usually a placeholder awaiting
  its SOW.

## Setting the go-live date
The timeline uses the milestone ticked as go-live on **Project Schedule** — open the milestone and
tick *This milestone is the go-live*. Failing that it falls back to the project's target end date,
then to the last dated row in the schedule, and marks the result with a tilde.

Existing milestones were flagged automatically from their titles, which deliberately skipped things
like *Go-Live Authorization* and *Aumentum SW Maintenance (at Go Live)* — a gate and a payment, not
the event. If a project's go-live looks wrong, tick the right milestone and it will follow.

## Moving around
**12 months** is the default view; **24 months** and **All** widen it. Every view opens with today
centered, marked by the gold dashed line. The timeline scrolls sideways while the project columns stay
pinned. Click any column heading to sort by it.

## Drilling into a project
Click a row to open it. The checkboxes on the left choose what that project's panel draws:
- **Show workstreams** — BPA, Conversion, Client Testing and the rest, each spanning the dates of its
  own tasks and milestones, with its own percentage complete.
- **Show phases** — the same work grouped the other way, by methodology phase. Both can be on at once.
- **Show milestones** — a marker per milestone, using the same shapes as Project Schedule. Hover one
  for its name, date and status. Markers sit on the workstream they belong to, or on the phase when
  workstreams are hidden; anything belonging to neither collects on an **Other milestones** row.

These settings are per project, so opening a second one starts fresh. **Open in Project Schedule**
jumps straight to that project's full plan.

A project with no phases, workstreams or dated milestones says so rather than drawing an empty chart
— its schedule hasn't been promoted from its SOW yet.
`,
    related: ['dashboard', 'project-schedule', 'schedule-views'],
  },
  {
    id: 'sow-intake',
    title: 'SOW Intake',
    category: 'Project management',
    audience: 'all',
    // Not '/project-launch' -- that's Project Setup's route, and the two can't both claim it without
    // one silently winning. '/intake' is the old bookmark's redirect target, still a real route.
    routes: ['/intake'],
    keywords: [
      'sow', 'statement of work', 'upload', 'extract', 'ai', 'milestones', 'contract',
      'placeholder', 'promote', 'load sow', 'reviewer', 'conflict', 'resolve differences',
    ],
    summary: 'Upload a signed SOW, let the AI read it, check the fields, and commit the project.',
    body: `
SOW Intake isn't its own page any more — it opens from Project Setup, one of two ways:

- **From a specific placeholder.** Open it under Project Setup → select the placeholder → **Load a
  SOW**. The system already knows which project this SOW belongs to, so there's no picker — see
  *Placeholder projects* below for what committing does to it.
- **For work nobody forecast in advance.** From the Project Setup project list, **Load SOW →** (top
  right) opens the same flow with no project locked in — pick a placeholder there instead, or leave it
  on **Create a new project**.

## The flow
1. **Upload the SOW.** It must be a **PDF** — Word documents aren't accepted.
2. **The AI reads it** and fills in the fields it can find: customer, contract value, dates, modules
   in scope, payment milestones.
3. **Review Extracted Fields.** Everything is editable. AI-populated fields are outlined so you can
   see what came from the document rather than from you. **Reviewer** is a picker, not free text —
   it defaults to you, but change it if someone else did the review.
4. **Enter an Estimated Start Date** — required before you can commit.
5. **Resolve differences**, if you opened this from a placeholder that already had data of its own.
   You're only asked about fields where the placeholder's value and the SOW's actually disagree —
   everything else merges automatically, and if nothing conflicts this step doesn't appear at all.
6. **Commit.** The project's schedule is built from the SOW's milestones at the same time, and the
   checklist's **Review SOW** item is marked complete under the reviewer you picked. Either the
   placeholder becomes this project, or a new one is created — the button says which.

## Reading the extraction
Milestones come back as a table you check row by row against the SOW's own payment schedule. Figures
are real numbers, so a surprising value is worth a look at the **Ambiguities** field — that's where
the AI records what it wasn't sure about, rather than guessing quietly.

Nothing is written until you commit, so re-reading and correcting costs nothing.
`,
    related: ['project-setup', 'placeholder-projects', 'kickoff-checklist', 'project-schedule', 'ai-settings'],
  },
  {
    id: 'project-setup',
    title: 'Project Details',
    category: 'Project management',
    audience: 'all',
    routes: ['/project-launch', '/setup'],
    keywords: [
      'setup', 'kickoff', 'folders', 'team', 'initialize', 'new project', 'setup status',
      'select project', 'missing project', 'project launch', 'needs setup', 'modules in scope',
      'kpis', 'health', 'milestone summary', 'filter milestones', 'travel information',
      'per diem', 'expense reporting', 'paste image', 'screenshot', 'project event', '30/60/90',
      'pm activity', 'billing milestones', 'collapsible', 'phase', 'workstream', 'go-live badge',
    ],
    summary: "A project's day-to-day hub, one-time launch checklist, and PM activity view — one page, three views.",
    body: `
Pick a project, then switch between three views with the links in the top right — **Project
Details** (the default), **Project Setup** and **PM Activity**. Switching views keeps the same
project selected.

## Project Details (default view)
The ongoing hub for a project already underway:

- **Project Info** — the identity card: name, customer, product, status, dates, and the module/lead
  table, behind one Edit toggle.
- **KPIs** — five PM-rated categories (Budget, Scope, Schedule, Client, Overall), each a dropdown
  plus a short comment, one Save. Overall's comment is logged as history rather than overwritten —
  **Show history** below it to see past entries.
- **Budget** — a read-only summary; **Manage →** opens the full Project Budgets page for the real
  line-by-line editing.
- **Next 30/60/90** — everything dated against this project over the next 90 days (the same feed
  Project Calendar draws on), bucketed instead of laid out on a grid. **+ Add Project Event** logs a
  stakeholder meeting, blackout period or decision deadline straight from here — see *Project
  Events*. A **Past due / no date** column beside the three buckets lists open RAAIDD items with no due
  date or one already past, so undated items can't hide; click one to open the register.
- **Milestones** — **Milestone summary** (% Complete, and how many milestones are Complete /
  Overdue / Upcoming) sits above the full milestone table. **Click a count tile to filter the table
  to just those milestones**; click it again to clear the filter. The table here is read-only —
  editing lives on the Project Setup view below — but its columns sort and drag-to-reorder (default
  order: Seq, #, DAS #, Milestone, Amount, Status, Baseline, Target, Actual, Submitted, Signed,
  Invoiced, Paid, Acceptance Criteria), and the header stays put while you scroll a long list. A
  green **Go-Live** badge next to a milestone's name marks the one flagged as the go-live event.
- **Travel Information** — Airport, Hotel, Per Diem, Expense Reporting and Other Travel Notes.
  **Paste a screenshot directly into any of these boxes** (Ctrl+V) to attach it — a hotel
  confirmation, a per-diem rate table. Click a thumbnail to view it full-size; the ✕ removes it.

## Project Setup
The one-time launch checklist. **Needs Setup** (Placeholder and Startup) is shown by default; T&M
and other Placeholder projects are a checkbox away if you need them, and anything Active, Completed
or Archived has no setup left to do and isn't listed there. **Load SOW →** at the top of the list is
for a SOW with no placeholder to belong to — see *SOW Intake*. Every frame on this view collapses
(click its title) — handy once a project has all of these filled in.

- **Placeholder actions** (placeholders only) — **Load a SOW** against this one, or (administrators)
  **Promote** it to a real project without a SOW.
- **Project Details** (this view's own identity form) — name, product, RAG status, dates, and
  **Software Modules In Scope** as a tree you check off from the module taxonomy (Project
  Configuration → Modules maintains that list) rather than typing free text. Checking a submodule
  checks its parent module too.
- **Billing Milestones** — the one fully editable milestone grid (the Project Details view above only
  shows a read-only copy). Every row edits in place — no separate edit step — with **+ Add** and
  **Save** at the top of the grid and **⧉** to duplicate a row (an unsaved copy at the top — rename it, then Save; the #, DAS # and dates
  history aren't copied) and **✕** to delete a row inline. Columns sort and drag-to-reorder,
  same as the read-only table, but **Phase** and **Workstream** only show up here (both are dropdowns —
  pick a blank to unassign) and lead the default column order, ahead of Seq, #, DAS #, Milestone, Amount, Status, and the rest. **Go-Live** isn't a column —
  it's a checkbox under the milestone name. **Category** and **Value** are dropdowns (billing
  category — PS/R&D/License/Hosting/MA; value type — Committed/Draw-down/Optional) that drive
  *Milestone Tracker*'s financial view. **Baseline schedule** stamps today's target dates as the
  baseline the Tracker's Slip column measures against — the first run only baselines rows with none
  yet; running it again after a change order offers to re-baseline everything (asks why) or leave
  already-baselined rows alone. It then asks what to call the baseline (leave it blank for the next
  number) and keeps a numbered copy of the whole plan, so a project builds up Baseline 1, Baseline 2
  and so on rather than overwriting one. **View baselines** beside it opens them side by side — see
  *Baselines & snapshots*. A baseline of a plan nothing has changed in isn't captured, since it would
  only duplicate the last one.
- **Repeats (recurring milestones)** — for a line the SOW bills monthly, such as Project Management,
  keep **one** row instead of one per month. Set **Target** to the first month, **Repeats** to
  *Monthly*, **Last period** to the final month, and **Periods done** to how many months are complete
  (raise it each month). **Amount** is the *total* across all months, not the monthly figure; the
  tracker splits it evenly, with the last month taking any rounding. Switch back to *One-time* to turn
  it off. There is no per-month date or billing detail: a skipped or late month can't be recorded
  separately, and Slip isn't shown for these rows.
- **Moves** — a count per milestone of how many times its dates have been edited by hand; click it
  for the full trail, every edit and every baseline behind it. A **+** means it also moved before
  date logging existed in October 2026, so the real count is higher.
- **Assign Team** — conversion lead and a module-lead picker for every module currently in scope;
  adding a module above adds its lead picker here automatically.
- **Folder Structure** — review and edit the folder tree before it's created. It tracks Software
  Modules In Scope automatically (add a module, its folder appears; remove one, its folder goes)
  until the structure is actually created — after that, module changes no longer touch the real
  folders.
- **Initialize** — creates the SharePoint folder structure and the customer share site for real.
- **The checklist** — see *Project Launch Checklist*.

## PM Activity
Checklist activity across every PM's projects — a portfolio view, not scoped to one project.
Visible only to managers, PMs with projects of their own, and admins.
`,
    related: ['sow-intake', 'kickoff-checklist', 'placeholder-projects', 'project-configuration', 'project-registers', 'project-calendar', 'milestone-tracker'],
  },
  {
    id: 'setup-initialize',
    title: 'Initialize: folders and the customer site',
    category: 'Project management',
    audience: 'all',
    parent: 'project-setup',
    routes: [],
    keywords: ['initialize', 'folders', 'sharepoint', 'customer site', 'provisioning', 'share site'],
    summary: 'What the Initialize step actually creates, and how long it takes.',
    body: `
Two real actions, each with its own button and its own status:

- **Create SharePoint folder structure** — the internal project folders, under the customer's folder.
  Once it succeeds, the step turns into a link straight to them.
- **Create customer share site** — the external site (\`<customer>-External\`) with the five standard
  document folders. **This one is genuinely slow** — provisioning can take several minutes. The page
  polls every 10 seconds and updates itself; you don't need to sit on it, and pressing the button again
  won't make it faster.

Both are safe to re-check: the project record stores whether each one has been done, so a page reload
shows the real state rather than starting over. If a step fails, the message says which call failed —
worth reading before retrying, since a permissions problem won't fix itself on a second attempt.
`,
    related: ['project-setup', 'kickoff-checklist'],
  },
  {
    id: 'kickoff-checklist',
    title: 'Project Launch Checklist',
    category: 'Project management',
    audience: 'all',
    parent: 'project-setup',
    routes: [],
    keywords: ['kickoff', 'checklist', 'ready', 'launch', 'review sow', 'raaidd', 'change control', 'contact list'],
    summary: 'The real, saved checklist at the bottom of Project Setup — grouped, editable, and partly automatic.',
    body: `
Every project gets the standard 12-item checklist the first time you open its Project Setup, grouped
into **Project Planning**, **Resource Assignments**, **Project Metrics**, **Kickoff(s)** and **Project
Administration**. Unlike the old kickoff checklist, this one is real data — it survives leaving the
page.

Any item is editable in place: title, category, status, a due date, notes, and a document link (🔗) to
the actual artifact once one exists. Add your own items where the standard list doesn't cover
something project-specific.

Several items are launch buttons straight to the Hub page that replaced a spreadsheet — **Create
RAAIDD spreadsheet**, **Create Change Control Log** and **Create Contact List** open the matching tab
in Project Registers, and **Create Project Plan** opens Project Schedule.

**Four items complete themselves:**
- The two SharePoint-creation items flip to Complete once Initialize actually creates that folder
  structure or customer site — or once the project has moved past Startup at all, for older projects
  whose columns were never backfilled.
- **Review SOW** completes itself the moment a SOW is committed against this project, credited to
  whoever was picked as Reviewer during intake.

An auto-completed item shows no credited person — that's how you can tell it was the system, not a PM,
that checked it off.
`,
    related: ['project-setup', 'setup-initialize', 'sow-intake', 'bpa'],
  },
  {
    id: 'project-schedule',
    title: 'Project Schedule',
    category: 'Project management',
    audience: 'all',
    flag: 'scheduling',
    routes: ['/schedule'],
    keywords: [
      'schedule', 'gantt', 'phases', 'milestones', 'tasks', 'dependencies', 'populate from sow',
      'create from template', 'template', 'snapshot', 'baseline',
      'archived', 'show archived projects', 'project picker', 'no schedule',
    ],
    summary: 'Phases, milestones and tasks for one project, as a Gantt chart you can edit.',
    body: `
Pick a project at the top; below it is that project's schedule as a Gantt chart. Three views: **Phase**,
**Workstream**, and **Plan** (the editable one).

## Choosing a project
The page opens on **no project selected** — nothing loads until you pick one, so you're never looking at
a schedule you didn't ask for.

The list is filtered to projects you'd actually schedule:

- **Archived projects are hidden.** Tick **Show archived projects** beside the picker to bring them back;
  they're then marked *Archived* in the list.
- **T&M projects aren't offered at all**, and the toggle doesn't bring them back. Time-and-materials work
  is billed against hours rather than against a plan, so there's nothing here to build. Change the
  project's type on Project Configuration if that's wrong.
- **A ◇ in front of a name** means that project has nothing scheduled yet.

## Building a schedule
Two buttons, both offered only while the project has nothing scheduled yet:

- **Populate from SOW** builds phases, milestones and tasks from the SOW's own milestone list rather
  than making you type it out.
- **Create from Template** builds the whole thing from a methodology template instead — use it when
  there is no SOW to read, or when the SOW lists what gets paid for but not the work behind it.

Either way, suggested dates are anchored on the project's start date and are deliberately rough — a
starting point to adjust, not a commitment.

**Clear project schedule** is the start-over button: neither builder overwrites an existing schedule,
so clearing first is how you re-run one. It lives at the **foot of the Plan (edit) view**, not in the
toolbar — you have to scroll past the whole plan to reach it, which is deliberate. There's no undo.

## Snapshots
Once a schedule exists, **Snapshots** saves a frozen copy of it under a name you choose — see the
Schedule snapshots topic.

## Editing
Drag or edit dates, add and reorder items, set dependencies. The app refuses edits that would make a
schedule contradict itself (a task outside its phase, a dependency pointing backwards), so a failed
edit is usually the schedule protecting itself — read the message rather than working around it.

With no dates at all, the schedule renders as an indented outline instead of a chart. That's the empty
state, not an error.

Milestone amounts aren't shown here — money arrives with the Financials module.
`,
    related: [
      'sow-intake', 'project-configuration', 'portfolio-timeline', 'project-calendar',
      'create-from-template', 'schedule-snapshots',
    ],
  },
  {
    id: 'schedule-views',
    title: 'Phase, Workstream and Plan views',
    category: 'Project management',
    audience: 'all',
    flag: 'scheduling',
    parent: 'project-schedule',
    routes: [],
    keywords: [
      'phase view', 'workstream view', 'plan view', 'gantt', 'collapse', 'expand all',
      'frozen column', 'today', 'legend',
    ],
    summary: 'Three ways to read the same schedule, and which one you edit in.',
    body: `
- **Phase view** — the schedule banded by methodology phase. The default read: where is this project up
  to.
- **Recurring milestones draw as a bar, not a diamond** — a line billed monthly (set up on **Project
  Setup → Billing Milestones**) runs from its first to its last month with a tick per month and the
  completed share filled in.
- **Workstream view** — the same work grouped by stream (BPA, Conversion, Client Testing and so on),
  which is how the leadership timeline is drawn. Commercial streams pin above the phase bands, ongoing
  services below.
- **Plan (edit) view** — the editable one: a spreadsheet with a row per task *and* per milestone.
  Milestones are the rows with a violet diamond and a bold name, placed after their phase's tasks;
  edit the name, phase, workstream, target date and status in the row (marking one Delivered also
  completes the task that delivers it). Tasks have a Workstream column too. The **⧉** button on any
  row adds a copy named "… (copy)" and puts the cursor in its name, so you can rename it instead of
  re-typing the row. **Show milestones** above the table hides the milestone rows when you only want
  tasks. Everything is stored either way; the views differ only in how it's grouped and what you can change.

## Reading a long chart
Real plans run to a hundred rows and several years, so the Phase and Workstream views are built to stay
legible at that size:

- **Everything starts collapsed.** The bands *are* the overview; click one to open it, or use
  **Expand all**. Picking a different project collapses everything again.
- **The chart opens centered on today**, not on the project's first month — on a plan that started years
  ago, the part you care about is on screen immediately.
- **The name column is frozen.** Scroll sideways as far as you like; the phase, task and activity names
  stay put.
- **The date header stays at the top** as you scroll down — the month row in the Phase and Workstream
  views, and the Start/End column headings in the Plan view.
- **The legend stays at the bottom** as you scroll, so the milestone shapes and bar colors are always
  there to check against.

With no dates on anything, all three fall back to an indented outline. That's the empty state, not a
broken chart.
`,
    related: ['project-schedule', 'phases-and-workstreams', 'schedule-editing'],
  },
  {
    id: 'schedule-editing',
    title: 'Editing tasks, milestones and dependencies',
    category: 'Project management',
    audience: 'all',
    flag: 'scheduling',
    parent: 'project-schedule',
    routes: [],
    keywords: [
      'task', 'milestone', 'dependency', 'status', 'blocked', 'percent complete', 'finish to start',
      'go-live', 'golive',
    ],
    summary: 'Statuses, marking the go-live, the four dependency types, and why an edit gets refused.',
    body: `
## Statuses
Tasks are **Not Started / In Progress / Complete / Blocked**. Milestones use a different set —
**Not Started / In Progress / Delivered / Accepted / Missed** — because a milestone is *accepted by the
customer*, not worked. *Delivered but not Accepted* is a real and important state; don't collapse it
into Complete.

## Marking the go-live
A milestone's panel has **This milestone is the go-live**. Ticking it is what the **Portfolio Timeline**
anchors the project's bar and its ◆ marker to, so it's worth getting right — untouched, that page falls
back to the project's target end date and flags the result as inferred.

Tick the milestone that *is* the event, not the paperwork around it: *Aumentum Platform Go-Live Day*
rather than *Go-Live Authorization*. A segmented project can have more than one; the timeline's bar
ends at the last, and each gets its own marker.

## Dependencies
**Finish-to-start** covers almost everything and is the default. Start-to-start, finish-to-finish and
start-to-finish exist because the schedules migrated in from OnePlan use them. Moving a task pushes its
dependents along with it, so a single date change can cascade — check the chart after a big move.

## When an edit is refused
The app blocks edits that would make a schedule contradict itself — a task pushed outside its phase, a
dependency pointing backwards. Read the message rather than working around it: it's naming a real
conflict, and the schedule it's protecting is the one the customer sees.
`,
    related: ['project-schedule', 'schedule-views', 'phases-and-workstreams'],
  },
  {
    id: 'populate-from-sow',
    title: 'Populate from SOW, and starting over',
    category: 'Project management',
    audience: 'all',
    flag: 'scheduling',
    parent: 'project-schedule',
    routes: [],
    keywords: [
      'populate from sow', 'clear schedule', 'promotion', 'suggested dates', 'rebuild',
      'fill gaps from template', 'standard tasks', 'gap fill',
    ],
    summary: 'Building a schedule from the SOW, and the only way to re-run it.',
    body: `
**Populate from SOW** builds phases, milestones and tasks from the SOW's own milestone list. It also
runs automatically when a SOW is committed on the Intake page, so a new project usually has a schedule
before anyone opens this screen.

- **Suggested dates are deliberately rough** — anchored on the project's start date and spread by the
  methodology, not negotiated. Adjust them; don't quote them.
- **How a milestone gets its phase:** from the milestone's own wording where the SOW names one,
  otherwise inferred. A phase that only exists in a custom template can't be inferred from SOW prose —
  it appears only if a milestone lands there some other way.
- **It won't overwrite.** With a schedule already in place, Populate from SOW does nothing.

### Fill Gaps from Template
A SOW itemises the work for some phases and says nothing about others. Tick **Fill Gaps from Template**
and the phases the SOW is silent on get that template's standard tasks and sign-off milestones. The
phases it does itemize are left exactly as the SOW wrote them, because the SOW is the contract.

The **Template** box beside it picks which methodology to fill from. Leave it on *Project default* and
you get the project's own template, or the one marked default if it has none. Choosing a different one
here affects this run only — it does not repoint the project.

If nothing gets filled in and you get a warning about no project template, there isn't one to fill
from: create one on Project Configuration and mark it the default. (This option used to be called *Fill
gaps with standard tasks* and read from a fixed list in the code. It is template content now, so the
standard tasks are yours to edit.)

**Clear project schedule** is therefore the start-over button — it deletes the schedule so you can
re-run population. You'll find it at the **foot of the Plan (edit) view**, below the plan table. There is
no undo, and manual edits go with it.
`,
    related: ['project-schedule', 'sow-intake', 'project-templates', 'create-from-template'],
  },
  {
    id: 'create-from-template',
    title: 'Create from Template',
    category: 'Project management',
    audience: 'all',
    flag: 'scheduling',
    parent: 'project-schedule',
    routes: [],
    keywords: [
      'create from template', 'template', 'methodology', 'no sow', 'conditional phase',
      'duration weight', 'build schedule',
    ],
    summary: 'Building a whole schedule from a methodology template, with no SOW involved.',
    body: `
**Create from Template** sits beside *Populate from SOW* and appears on the same terms — only while the
project has nothing scheduled. Use it when there is no SOW to promote, or when the SOW lists billing
milestones but not the work behind them.

It creates the template's phases, workstreams, tasks and sign-off milestones, plus any links the
template defines between them.

- **Pick the template** at the top. The one marked *default* is pre-selected.
- **Preview first.** Nothing is written until you press *Create* — the preview lists every row, and the
  count on the button is what will actually land.
- **Untick phases you don't need.** Every phase appears as a checkbox once you preview. **Conditional**
  phases — the ones a template marks as "only some projects", like Software Development — start
  unticked; tick one to include it. Everything under an unticked phase goes with it.
- **Dates come from the project's start date and duration**, split between phases in the proportions
  the template sets, and each task gets a slice of its phase sized by the duration the template gives
  it. No start date on the project means no dates — you get a warning rather than invented ones.
- **Duration override** is there for when the SOW prose and the project's target end date are both
  unusable.

Afterwards the project is pinned to the template you built from, so *Fill Gaps from Template* on a
later run defaults to the same methodology.

Re-running it is safe: anything already there under the same name is skipped, not duplicated.
`,
    related: ['project-schedule', 'populate-from-sow', 'project-templates', 'schedule-snapshots'],
  },
  {
    id: 'schedule-snapshots',
    title: 'Baselines & snapshots',
    category: 'Project management',
    audience: 'all',
    flag: 'scheduling',
    parent: 'project-schedule',
    routes: [],
    keywords: [
      'snapshot', 'snapshots', 'baseline', 'baselines', 'frozen copy', 'schedule baseline',
      'save schedule', 'line in the sand', 'compare baseline', 'baseline comparison', 'variance',
      'slip', 'moved', 'moves', 'date history', 'delivered late', 'what changed',
    ],
    summary:
      'Every baseline this project has committed to, what has moved since each one, and whether '
      + 'milestones were actually delivered on time.',
    body: `
**Baselines & snapshots** appears in the Project Schedule toolbar once a project has a schedule. Both
are the same thing — a frozen copy of every phase, milestone and task exactly as they stood at one
moment — and they differ only in what they're for:

- A **baseline** is a plan you committed to. You create one from **Project Launch → Billing
  Milestones → Baseline schedule**, which stamps the target dates *and* captures the baseline in one
  go. They're numbered: Baseline 1, Baseline 2, and so on.
- A **snapshot** is one you save by hand here, for your own reference, without committing to it. Give
  it a **name** you'll recognize later ("Post-BPA re-plan"), add **notes**, and press *Save snapshot*.

Neither ever changes afterwards — that's the whole point. Names inside are captured too, so a phase
renamed next year still reads as what it was called at the time. Names are unique per project, and
**Delete** asks first with no undo.

### Every baseline side by side

The grid at the top of the panel puts one column per baseline against each milestone, so you can read
a single milestone's whole history across a row. After the baseline columns:

- **Current target** and **Actual** — where the plan stands now, and when it was actually delivered.
- **Target vs first / Target vs latest** — how far the *plan* has moved, from the first baseline that
  carried that milestone and from the most recent one. These say nothing about delivery.
- **Actual vs target / Actual vs first** — whether it was *delivered* late, against the current plan
  and against what was originally committed to. Blank until it's delivered. The second is usually the
  number a customer means, because re-baselining is exactly what hides a slip: a milestone committed
  to 1 May, re-planned to 1 June and delivered 8 June is a week late against the plan and 38 days
  late against the promise.
- **Moves** — how many times the target date has been edited by hand, which is a bigger number than
  the baseline count, since most slips never earn a re-baseline.

Red is late, green is early, *on plan* means no change. A milestone that was deleted since a baseline
still appears, struck through — that captured row is the only record of descoped work.

**About the + on a Moves count.** Date edits have only been recorded since October 2026. Where a
milestone plainly moved before then — its baseline no longer matches its target — the count shows as
a floor ("0+", "3+") rather than a blank, because those edits were never recorded and can't be
recovered. A plain "3×" means the log saw the whole story. "—" means it genuinely hasn't moved.

### One baseline against the live schedule

**Compare** on any baseline or snapshot lines it up against the schedule as it stands now, row by
row: what **moved** and by how many days, what's been **added since**, what's been **removed since**,
and what's **unchanged**. Switch between milestones, phases and tasks; tick *Only what changed* to
drop the rows that didn't. The summary line gives the net slip across everything that moved — net,
not total, so one milestone slipping a week and another pulling in a week reads as no net change.
`,
    related: ['project-schedule', 'project-setup', 'milestone-tracker', 'create-from-template', 'schedule-views'],
  },
  {
    id: 'milestone-tracker',
    title: 'Milestone Tracker',
    category: 'Project management',
    audience: 'all',
    routes: ['/milestone-tracker'],
    keywords: [
      'forecasted values', 'power bi', 'billing category', 'value type', 'committed', 'draw-down',
      'optional', 'baseline', 'slip', 'burn-down', 'burndown', 'attainment', 'this month', 'poc',
      'percent of completion', 'contract value', 'tcv', 'calendar view', 'billing progress',
      'fcst', 'act', 'grain', 'month', 'quarter', 'billed as used', 'drawdown', 'das date',
      'undrawn balance', 'recurring', 'monthly', 'repeats', 'project management',
    ],
    summary: 'The financial view of every milestone across every project — replaces the Power BI '
      + '"Forecasted Values" report.',
    body: `
Across every project at once, not one at a time — this answers what the old Power BI "Forecasted
Values" report did.

- **Cards** across the top (Total contracted, Complete, Remaining, Due ≤30/31–60/61–90 Days,
  Overdue) show a count and a dollar amount together. Click one to filter the grid to just those
  milestones; click again to clear it.
- **Contract value** — total value split by billing category (PS, R&D, License, Hosting, MA) with a
  donut, plus the POC% (percent of completion, by dollars billed).
- **This month** — a gauge of what's due this month against what's actually been hit, by count and
  by dollar amount; the countable figures double as filters into the grid.
- **Billing progress** — year-over-year FCST vs ACT, years across the top.
- **Remaining to bill over time** — a burn-down: planned value against actual, so any gap is visible
  slippage.
- **List / Calendar toggle** — List is the milestone grid; Calendar pivots milestones into month or
  quarter columns with a total on the bottom row (the old Power BI grid). The Month/Quarter toggle
  also sets the burn-down's grain. Across all projects, the calendar keeps State, County, Project
  (and PM, if turned on) pinned on the left while the months scroll.
- **Columns menu** — show or hide grid columns, including an optional **Project Manager** column.
  It applies to the calendar too. Project columns lock off once a single project is picked.
- **Billing milestone email alerts** — the project's PM plus PS leadership get an email when an
  open, not-yet-invoiced PS, R&D or uncategorized milestone with an amount is **60, 30 and 10 days**
  from its target date, and once when it **goes past due**. Each alert goes out once; moving the
  target date starts them over. To stop alerts on a milestone, mark it Delivered or later, or enter
  its Date Invoiced.
- **Filters** — Project, Status, Track (FCST/ACT), Year, an arbitrary Target Date range, and a
  Billing Category filter (defaults to PS + R&D + Uncategorized — deselecting a category says how many rows it's
  hiding and what they're worth, so totals never quietly disagree with the contract).
- **A Draw-down milestone tied to a billed-as-used budget shows as several rows, not one.** Each
  line drawn against that budget becomes its own ACT row, dated by its **DAS submitted date** — the
  month the work was actually claimed — and what's left of the budget stays as one FCST row at the
  milestone's target date, labeled *— undrawn balance*. The rows still add up to the contracted
  amount, so nothing is counted twice. Tie a budget to a milestone on **Project Registers → Billed
  as Used**.
- **A recurring milestone is one row in the List** (for example *Project Management (monthly · 3 of
  12 done)*), with **Target** as the next month due and **Actual** the last month completed. Every
  total, the burn-down and the Calendar still count the individual months, and the Calendar shows it
  as one row with an amount in each month. Set one up on **Project Setup → Billing Milestones**
  (the **Repeats** column).
- **Slip column** — days between a milestone's baseline and where it landed (actual date if done,
  current target date if not). Positive is late, negative is early; blank means no baseline was ever
  set. Baselining itself happens on **Project Setup**'s Billing Milestones grid — see *Project
  Details*.
- **Moves column** — how many times that milestone's target or actual date has been edited by hand.
  Slip says how far off plan a date is; this says how restless it's been getting there, which is a
  different finding — a milestone that moved five times to land on plan isn't the same as one that
  never moved. A **+** ("0+", "3+") means it also moved before date logging existed in October 2026;
  those edits were never recorded, so the real count is higher. To see the moves themselves, and
  every baseline behind them, use **Project Schedule → Baselines & snapshots**.
- **Export** — every panel exports the same three ways: PDF, Email (HTML, paste into Outlook), or
  Excel.
`,
    related: ['project-setup', 'project-calendar', 'project-registers', 'schedule-snapshots'],
  },
  {
    id: 'revenue-tracker',
    title: 'Revenue Tracker',
    category: 'Project management',
    audience: 'all',
    routes: ['/revenue-tracker'],
    keywords: ['revenue', 'act vs fcst', 'variance', 'billing rate', 'crystallized', 'prior month', 'fcstsep', 'running total', 'heatmap', 'bridge'],
    summary: 'Revenue ACT vs FCST in dollars, against last month\'s locked forecast.',
    body: `
Revenue is **hours × the project's billing rate** in effect that month. Billing rates are set on
each project's budget page and keep their history, so a rate change only affects months from its
effective date on.

- **Compare against** — by default, Pulse's own forecast, the same one Time Reports' FCST vs
  Actuals uses. Resource Planning locks each month's forecast once the month starts, so it can't
  move after the fact. You can also pick a locked baseline (annual, quarterly or monthly).
- **Var — Month** — the month's actuals (to date, for the month in progress) vs its forecast.
- **FY vs last mo** — only when a baseline is picked: the full-year outlook vs that baseline's year.
- **Running total** — each month's variance added up through the year.
- **Colors** — green at or over forecast, orange up to the tolerance you pick under it (default 2%), red further under.
- **Layouts** — Tracker (list or calendar), Variance heatmap, and Bridge, which splits the change
  into an hours effect and a rate effect.
`,
    related: ['milestone-tracker'],
  },
  {
    id: 'project-calendar',
    title: 'Project Calendar',
    category: 'Project management',
    audience: 'all',
    routes: ['/calendar'],
    keywords: [
      'calendar', 'month view', 'week view', 'milestones', 'phases', 'tasks', 'bpa sessions',
      'raaidd', 'change register', 'billed as used', 'go-live', 'event types', 'project events',
      'add project event', 'stakeholder meeting', 'blackout period', 'decision deadline',
    ],
    summary: 'Every dated activity across one or more projects — milestones, phases, tasks, BPA '
      + 'sessions, register dates and project events — on one calendar.',
    body: `
Pick one or more projects at the top; the calendar fills with everything dated against them. Nothing
shows until you pick a project — same reasoning as Project Schedule's empty start.

## What shows up
Eight event types, each its own color (the legend above the calendar names them):

- **Milestones** — the target date, or the actual date once delivered (shown as *(completed)*). A
  milestone ticked as the go-live gets a red outline so it stands out from the rest.
- **Phases** and **Tasks** — their date range, from Project Schedule.
- **BPA Sessions** — the scheduled date; the session's wall-clock time (e.g. "9:30 AM – 11:00 AM")
  shows in the detail card, not as a precise time slot on the grid.
- **RAAIDD, Change Register, Billed-as-Used** — due/review dates and submitted/approved/paid dates
  from Project Registers. A register item with several dates set (a CR that's been both submitted
  and approved) shows once per date, each labeled with what happened.
- **Project Events** — stakeholder meetings, blackout periods, decision deadlines: see the Project
  Events topic. **+ Add Project Event** above the calendar opens the same form Project Details and
  the Project Events tab use, defaulting to whichever project(s) you have selected.

## Filtering
Both the **Projects** and **Event types** pickers are multi-select — tick as many as you want.
Leaving **Event types** untouched shows everything; there's no "select all" needed.

## Views
**Month** (the default) and **Week**. Daily detail isn't needed here — Project Schedule and BPA
Tracker are where you'd go for that.

## Opening the source
Click any event for a detail card — project, date(s), and for a register item, its description.
**View** jumps straight to the page that item lives on (Schedule, BPA Tracker, or the right Project
Registers tab) so this page is a way to *find* something, not a second place to edit it.

## What the customer sees
The portal has this same calendar for their own project, read-only, with the same event types and
the same month/week views. Three differences, all deliberate:

- **Their project only.** The API checks the contact's project grant before answering, so the
  picker there is a convenience rather than the boundary.
- **RAAIDD and Project Event items marked "Internal only" never reach it**, and neither do removed
  RAAIDD rows — the same exclusions those tabs already make. Everything else on this calendar is
  data they can already see on their schedule and registers.
- **Times are their county's own.** A 9:30 session reads as 9:30 on their portal whatever zone the
  reader is in, with the zone named once above the calendar. Yours shows it in *your* zone instead,
  because your calendar can span counties in several.

Use **View as Customer** in the header to see it exactly as they do.
`,
    related: ['project-schedule', 'project-registers', 'project-events', 'bpa', 'view-as-customer'],
  },
  {
    id: 'project-registers',
    title: 'Project Registers',
    category: 'Project management',
    audience: 'all',
    routes: ['/registers'],
    keywords: [
      'registers', 'contact list', 'contacts', 'change register', 'change control', 'cr',
      'billed as used', 'bau', 'drawdown', 'budget', 'das', 'raaidd', 'tabs', 'project events',
      'draws down milestone', 'draw-down', 'tie budget to milestone',
      'stakeholder meeting', 'blackout period', 'decision deadline', 'project meeting',
      'stakeholder', 'customer access request', 'pulse access', 'sharepoint access',
      'save as contact', 'import contacts', 'bulk import',
    ],
    summary: 'The six per-project logs — contacts, change requests, RAAIDD, billed-as-used, project events, customer access requests — on one page.',
    body: `
One page, six tabs, one project at a time. Each tab replaces a spreadsheet PMs used to keep and
email to the customer, and each is shared with the customer on their own portal.

**Adding or deleting a row never loses your other edits.** Clicking **+ Add** or the delete control on
a register saves any pending edits on the other rows first, so nothing you typed is thrown away. If one
of those pending edits is invalid, you'll see its error and the add/delete waits until it's fixed.

**Pick the project once, at the top** — it stays selected as you move between tabs. The list is
fixed-fee (POC) projects only, and leaves out archived projects until you tick "Show archived
projects". A ◇ beside a project name means nothing has been entered in *the tab you're on* yet, so
the same project can show a ◇ on one tab and not another.

**Every filter on this page is multi-select** — tick as many values as you like. Within one filter
the ticks widen what you see (Risks *and* Issues); across filters they narrow it. An empty filter
means "no filter", not "nothing".

**Project Contact List** — the joint project team, ours and the customer's, in one list.
Organization (Aumentum or Customer) is what separates the two sides; Office is a location. Emails
and phone numbers are clickable. Stakeholder is a plain flag for a contact whose sign-off matters
beyond their day-to-day role — it doesn't change anything else about the row. The cards count each
side of the team.

**Export template / Import…** on the Contact List's toolbar bulk-loads a project's team from a
spreadsheet — download the template, fill in a row per person, then Preview and Confirm import
before anything is saved. Three Y/N columns beyond the usual contact fields: **Save as Contact**
(N skips the Contact List for that row entirely — blank means Y, so an older template still behaves
the same as before), **Access to Pulse** and **Access to SharePoint** (either logs the row as a
Customer Access Request on the tab below; Pulse also adds the person to Configuration → Customer
Access so a PM can send them their portal invitation). The same Export/Import lives on the Customer
Access Requests tab too — same template, same behavior, just a second place to reach it from.

**Change Register** — the CRs raised against the project. CR # is assigned automatically, one
continuous sequence per project. Billing amount takes a negative number for a credit back to the
customer. The five dates track the CR through its life: CR submitted → CR approved → DAS submitted →
DAS approved → Date paid; the last three of those, plus Notes, are behind the ▸ on each row. Status
runs Draft → Submitted → Approved → DAS Submitted → DAS Approved → Paid, with Rejected and Canceled
as the two ways a CR stops. "In flight" counts everything not yet Paid, Rejected or Canceled, and
"Net billing impact" adds up the amounts on every CR except the rejected and cancelled ones.

**RAAIDD Register** — see the RAAIDD Register topic.

**Billed-as-Used Register** — the drawdown budget and what's been drawn against it. A project can
have more than one budget (a reports bucket and an interface bucket, say), so each one is its own
card with its own lines and its own running balance. Add the budget first — its starting dollars and
hours — then a line per deliverable as it's requested, delivered and invoiced.

- **The balance is calculated, never typed.** Remaining is the starting budget minus what the lines
  add up to, so it can't fall out of step with them.
- **Two readings of "hours remaining"**, and they can disagree. One is the hours sold minus the
  hours logged; the other is what the remaining dollars buy at the rate (starting budget ÷ starting
  hours). When they differ the second is shown in brackets — worth a look, because it means a line's
  dollars and hours weren't in step.
- **Leaving an amount blank is not the same as entering 0.** A blank line is scoped but not yet
  drawn against; a 0 means it was delivered at no cost. Blank shows as —, not $0.00.
- **Billed?** marks a line as invoiced. "Of which unbilled" on the balance is what's been delivered
  but not yet invoiced — the month-end figure.
- **Draws down milestone** ties the budget to the SOW line it bills against. Only milestones whose
  **Value** is *Draw-down* can be picked — a Committed milestone bills on acceptance, so it can't
  also bill a piece at a time. Once tied, every drawn line shows up on the **Milestone Tracker** as
  an actual on its **DAS submitted date**, instead of the whole budget landing on the milestone's
  target date. If the milestone's amount and the starting budget don't match, the card says so
  rather than picking one — someone has to decide which is right. This is staff-only: the customer's
  copy of the register doesn't show the link.
- **General notes** at the foot of the tab are the commercial rules for the budget: how hours can
  move between buckets, when they expire.

**Project Events** — see the Project Events topic.

**Customer Access Requests** — who's asked for SharePoint and/or Pulse (this app) access, per
project — replaces ad hoc "can you add so-and-so" emails. Anyone can add a row (staff here, any
project contact from the portal); only staff can edit one or tick it granted. Ticking **Pulse
granted** creates the real access — the same customer-account-and-project-grant Configuration →
Customer Access uses — so this tab and that admin screen can never drift on who actually has Pulse
access.
Ticking **SharePoint granted** is a plain record of something done outside the app; there's no
in-app SharePoint provisioning to trigger. Either way, granting never sends an invitation on its
own — that's still a separate, deliberate step on Configuration → Customer Access.

## What the customer sees

The portal has the same page, same six tabs, same order. Five of the six are **read-only** — the
same grids, summary cards and filters you're looking at, not a cut-down copy — with three
exceptions:

- **Anything removed.** A removed RAAIDD item, or a removed review update, never reaches them.
- **Anything marked Internal only.** That's the per-item exception on the RAAIDD and Project Events
  tabs; there's no equivalent on the other three read-only registers, so assume a contact, a CR or a
  drawdown line is visible the moment you save it.
- **Nothing they can change.** No add, edit, remove or "Show removed" anywhere — the API refuses a
  write from a customer login rather than the portal merely hiding the buttons.

**Customer Access Requests is the one exception to "read-only."** Any project contact can submit a
request for someone from the portal — a small name/email/SharePoint/Pulse form above the grid — even
though, like every other tab, they still can't edit an existing row or grant one themselves.

Their project list is whatever projects that contact has been granted (Configuration → External
Users), not the fixed-fee rule this page uses. Use **View as Customer** in the header to see it
exactly as they do.
`,
    related: ['risk-register', 'project-events', 'view-as-customer', 'project-schedule', 'bpa', 'project-calendar', 'milestone-tracker'],
  },
  {
    id: 'risk-register',
    title: 'RAAIDD Register',
    category: 'Project management',
    audience: 'all',
    // No `routes`: it's one tab of /registers, and that route opens the Project Registers topic,
    // which links here.
    keywords: [
      'risk', 'raaidd', 'action', 'assumption', 'issue', 'dependency', 'decision', 'log',
      'status', 'unresolved', 'filter', 'owner', 'risk matrix', 'level', 'impact', 'probability',
      'review', 'update', 'due', 'next review', 'overdue', 'remove', 'reinstate', 'internal only',
    ],
    summary: 'The RAAIDD log for a project, with review updates, due dates and a risk matrix.',
    body: `
One log per project for Risks, Actions, Assumptions, Issues, Dependencies and Decisions — replacing
the RAAIDD Register spreadsheet PMs used to email to customers directly. It's the third tab of
**Project Registers**; it used to be its own "Risk Register" nav item, and that link still works.

The grid is built to answer three questions without opening a row: **how bad is it** (Risk),
**when is it due** (Due, Next review), and **what is being done** (Latest update).

- **Log # is assigned automatically** — one continuous sequence per project across every type, so it
  never needs to be typed or renumbered.
- **The Risk column is the risk matrix**, not a field you type. It's Level × Impact banded back to
  Low / Medium / High: two Lows read Low, High × High reads High, and hovering it shows the Level,
  Impact and Probability % behind it.
- **Only Risks and Issues carry a rating.** An Action, Assumption, Dependency or Decision has
  nothing to rate, so Level, Impact and Probability % appear on the edit form *only* for those two
  types — which is why the three raw numbers came off the grid in favour of the one Risk cell. If you
  reclassify a Risk or an Issue to one of the other types, the form tells you the rating will be
  cleared, and clears it when you save; switch the type back before saving and it's kept.
- **Review updates are how the register shows something is being managed.** Add one from the ▸ on a
  row, or from the bottom of the edit form — both are the same list. Say what has happened, the date
  it happened, and optionally the next review date. Adding one never overwrites an older one; they
  stack newest-first. The newest shows in the grid's Latest update column with its date and who
  wrote it, and the search box looks inside the whole history.
- **Updates can be corrected and removed.** **Edit** on an update fixes its wording or its date (it
  does *not* move the row's Last reviewed — that's a field on the row itself). **Remove** takes an
  update off the history but keeps it on record, exactly as removing an item does: tick **Show
  removed** to see it grayed and marked Removed, with **Reinstate** to put it back. Customers never
  see a removed update.
- **Updates in the edit form save straight away**, separately from the form's own Save button — an
  update is its own record, not part of the draft, so Cancel won't undo one.
- **Adding an update moves Last reviewed on for you**, to that update's date. Backdating an update
  never moves it backwards, so writing up an old review can't make the register claim the item has
  been neglected.
- **Due and Next review turn red once they've passed**, on anything not Completed, Closed or
  Canceled. "Overdue a look" on the cards counts exactly those.
- **Which status to pick.** Not Started, Open and In Progress are all for work that's still live —
  Open is the default for something just logged, Not Started for something planned but not begun,
  In Progress once it's actually moving. Monitoring is for an item you're watching rather than
  working: an assumption still holding, a dependency not yet due. Completed, Closed and Canceled
  are the three that take a row off the unresolved count — Completed when the work got done, Closed
  when the item is settled, Canceled when it stopped mattering.
- **Nothing is ever deleted — items and updates are removed.** Remove takes something off the
  register but keeps it on record; tick **Show removed** to see both (grayed, marked Removed) and
  **Reinstate** to put one back. Customers never see anything removed.
- **Internal only** on the edit form hides one item from the customer portal. The register is
  customer-visible by default — unlike Business Process Analysis, the whole row is shared exactly as
  entered, review updates and all — and this is the per-item exception for something that shouldn't
  be. It's enforced by the API, not by the portal hiding a row, and it's the only field on this
  register that changes what the customer can see (removing an item is the other way to take
  something off their view).
- **Every filter is multi-select.** Tick as many Types, Statuses or Owners as you like: within one
  filter the ticks widen the result ("Risks and Issues"), and across filters they narrow it.
  "Unresolved (any)" covers Not Started, Open, In Progress and Monitoring in one tick, and combines
  with a specific status if you want both. The cards count the whole register, not what your filters
  show; whenever a filter is hiding rows, the grid says "Showing X of Y" above it.
- **Reclassifying an item** (changing its Type after the fact) records what it was first logged as in
  Original RAAIDD Type, the first time only.
- **On the portal it's the third tab of Project Registers**, read-only and showing the same columns,
  cards, filters and review history you see here — minus internal-only items and anything removed.
  See "What the customer sees" under Project Registers.
`,
    related: ['project-registers', 'bpa'],
  },
  {
    id: 'project-events',
    title: 'Project Events',
    category: 'Project management',
    audience: 'all',
    // No `routes`: it's a tab of /registers, same as RAAIDD — that route opens the Project
    // Registers topic, which links here.
    keywords: [
      'project event', 'stakeholder meeting', 'project meeting', 'blackout period',
      'decision deadline', 'executive review', 'event type', 'internal only', 'project calendar',
      '30/60/90',
    ],
    summary: 'Dated markers with real project impact but no home on the Schedule, a register, or a BPA session.',
    body: `
A project event is something happening during a project that isn't on the Project Schedule, a
register, or a BPA session — but still matters enough to track and see coming: a stakeholder
meeting, a blackout period, a decision deadline. It's the fifth tab of **Project Registers**.

- **Title, Event Type, Start Date, and an optional End Date** (for something that spans days, like a
  blackout period — a single-day meeting or deadline just leaves it blank). **Event Type** is a free
  text box with common suggestions (Project Meeting, Stakeholder Meeting, Blackout Period, Decision
  Deadline, Executive Review, Other) — type your own if none fit.
- **Add one from three places**: this tab, the **Next 30/60/90** frame on Project Details, or
  **Project Calendar** — all three open the same form, so it doesn't matter which you use.
- **Shows on Project Calendar automatically**, titled with its type ("Blackout Period: County
  offices closed"), no separate step needed.
- **Internal only** hides one event from the customer portal, same as the RAAIDD register's
  per-item exception — events are customer-visible by default. It's enforced by the API, not by the
  portal hiding a row.
- **Nothing is soft-deleted here** — Delete removes an event for good, unlike RAAIDD's Remove/
  Reinstate. There's no history to preserve on a meeting date the way there is on a risk's review
  trail.
- **On the portal it's the fifth tab of Project Registers**, read-only, minus anything marked
  Internal only.
`,
    related: ['project-registers', 'project-calendar', 'project-setup'],
  },
  {
    id: 'phases-and-workstreams',
    title: 'The methodology: phases and workstreams',
    category: 'How things work',
    audience: 'all',
    routes: [],
    keywords: ['phases', 'workstream', 'methodology', 'initiation', 'execution', 'closure', 'stage'],
    summary: 'The eight phases and seven workstreams every project schedule is built from.',
    body: `
**Phases** are sequential stages: Contract & Mobilization, Software Development (only for projects with
custom development), Initiation & Planning, Definition & Analysis, Execution, Performance & Control,
Production & Closure, and Ongoing Services.

They're grouped into three stages — **pre-project**, **core**, and **post-project** — which is what puts the
contract and ongoing-services bands outside the main project body on the chart.

**Workstreams** cut *across* phases: Project Planning, BPA, Development, Conversion, Migration, Client
Testing, Client Training. A workstream is also one of three kinds — **delivery** (the streams above),
**commercial** (contract and billing), or **ongoing** (post-go-live support and consulting) — which decides
where it sits on the timeline.

Both lists come from the project template, so an administrator can change them for future projects
(*Project Templates*). Changing a template doesn't reshape projects already created from it.
`,
    related: ['project-schedule', 'project-templates', 'bpa'],
  },

  // ---------- Project implementation ----------
  {
    id: 'bpa',
    title: 'Business Process Analysis',
    category: 'Project implementation',
    audience: 'all',
    routes: ['/bpa'],
    keywords: ['bpa', 'business process analysis', 'sessions', 'discussion', 'modules', 'topics', 'onsite'],
    summary: 'Run a project\'s BPA: sessions, the topic library, and the discussion record.',
    body: `
Replaces the stack of per-team BPA workbooks with one grid per project.

## Picking a project
The picker lists **fixed-fee (POC) projects only** — T&M is hourly work, and the booking buckets
(*Non-Billable Time*, *Misc (Plug)*) aren't projects anyone runs a workshop for. A **◇** in front of a
name means that project has no BPA sessions yet. Archived projects are hidden until you tick
**Show archived**.

## The workflow
1. **Seed the project's modules** from the SOW (*Modules & seeding*). Anything in the SOW's module list
   it can't place is listed for you rather than dropped silently.
2. **+ Session** adds a session — a scheduled BPA meeting for a module. Tick **Onsite** when the BAs are
   traveling for it.
3. **Work the discussion rows** under each session. They come from the standard topic library, so the
   same questions get asked on every project.
4. **Record the answers** as you go, in the meeting.

## Using the grid
It's built to be used like a spreadsheet — type, tab, move on. **Meeting link** and **Recording link** are
in the order you use them; the recording column exists because the SOW obliges us to keep one. The **↗**
opens a saved link in a new tab.

**Internal notes** are a separate column from the customer-facing answers and stay internal.

**Statuses** track each row's progress. For something a customer genuinely doesn't do, set
**Not Applicable** rather than deleting the row — the deletion is unrecoverable and loses the fact that
you asked.

Counters at the top are worked out live from the rows in front of you — **they follow the filters**, so
filtering to one module gives you that module's numbers, and a line under them says how many of the
project's sessions they cover. The module filter's **Tax - All Submodules** option is a rollup, not a
single module — it shows Tax plus every one of its submodules (AR, Cashiering, Delinquents,
Distribution, Levy, Special Assessments, Tax Accounting) at once, for when you want the whole Tax side
of a project without picking through each submodule separately. **Remove unstarted sessions**
(in *Modules & seeding*) tidies up sessions nobody ever touched. Deleting a session deletes its
discussion rows with it, with no undo.

A project's BPA can run in more than one **iteration** — later passes are tracked separately rather than
overwriting the first.
`,
    related: ['bpa-library', 'project-schedule', 'bpa-sessions', 'bpa-discussion-grid', 'project-calendar'],
  },
  {
    id: 'bpa-sessions',
    title: 'BPA sessions and seeding',
    category: 'Project implementation',
    audience: 'all',
    parent: 'bpa',
    routes: [],
    keywords: ['session', 'seed', 'modules', 'iteration', 'onsite', 'not scheduled', 'cancelled'],
    summary: 'Creating the session list from the SOW, and what each session status means.',
    body: `
**Modules & seeding** creates a session for every topic the project's modules imply — most with no date
yet, which is why the default status is *Not Scheduled* rather than a blank. A blank date used to be
indistinguishable from a topic nobody had thought about; now it isn't.

## Statuses
**Not Scheduled → Scheduled → Completed**, plus **Follow-up Needed** for a session that ran but didn't
finish the questions, **Canceled**, and **Not Applicable** for a module this customer doesn't use.

## The rest of the toolbar
- **+ Session** adds one by hand; it appears at the top of the list. If the module filter is set, the new session is created under that module (under Tax for *Tax - All Submodules*) so the filter doesn't hide it; any text or status filter is cleared.
- **Onsite** is a checkbox, not a status — tick it if the BAs are traveling for that session.
- **Remove unstarted sessions** clears out seeded sessions nobody ever touched, for when the module
  list was wider than the project turned out to be.
- **Deleting a session deletes its discussion rows too**, with no undo and nothing kept.

**Iterations:** a project's BPA can run in more than one pass. Later passes are tracked separately
rather than overwriting the first, so the original answers stay readable.
`,
    related: ['bpa', 'bpa-discussion-grid', 'bpa-times'],
  },
  {
    id: 'bpa-discussion-grid',
    title: 'The BPA discussion grid',
    category: 'Project implementation',
    audience: 'all',
    parent: 'bpa',
    routes: [],
    keywords: ['discussion', 'columns', 'customer questions', 'internal notes', 'open', 'decided', 'deferred', 'frozen columns'],
    summary: 'What each column is for, the row statuses, and getting around a wide grid.',
    body: `
Each session opens into its discussion rows — the standard questions for that topic, so the same ground
gets covered on every project.

## The columns
**Discussion points** is what you intend to cover. **Customer questions** and **Customer response** are the
customer-facing pair — what was asked, what they said. **Internal notes** is a separate column and stays
internal; put the "we'll need to check whether the product can do this" comments there, not in the
response. **Meeting link** and **Recording link** are in the order you use them, with **↗** to open a saved
link in a new tab; the recording column exists because the SOW obliges us to keep one.

## Row statuses
**Open → Discussed → Decided**, plus **Deferred** and **Not Applicable**. Use *Not Applicable* rather than
deleting a row a customer genuinely doesn't do — the library stays intact for the next project, and the
fact that you asked is recorded.

## Getting around
Use it like a spreadsheet: type, tab, move on. The leading columns stay frozen as you scroll right, and
**Expand/Collapse all** opens every session at once. The counters at the top are worked out live from the
rows in front of you.
`,
    related: ['bpa', 'bpa-sessions', 'bpa-library'],
  },
  {
    id: 'bpa-times',
    title: 'Entering BPA session times',
    category: 'Project implementation',
    audience: 'all',
    parent: 'bpa',
    routes: [],
    keywords: ['time', 'timezone', 'zone', 'local time', '930', 'am', 'pm'],
    summary: 'Times are in the customer\'s local time, and you can type them the fast way.',
    body: `
Session times are in **the customer's own local time**, with the zone stated once above the grid rather
than repeated in every cell — it's derived from the customer's state.

**Type them the fast way.** \`930\`, \`9:30a\` and \`1300\` all work; they're tidied up when the row saves.
Something unparseable is left as you typed it rather than rejecting the whole grid's save, so a cell
that still looks odd after saving didn't parse — retype it.

For a customer in a state that spans two zones, the note above the grid says so explicitly instead of
guessing. Check the zone before sending invitations.
`,
    related: ['bpa', 'bpa-sessions'],
  },
  {
    id: 'config-tracker',
    title: 'Config Tracker',
    category: 'Project implementation',
    audience: 'all',
    routes: ['/config'],
    keywords: ['config', 'configuration', 'config tracker', 'setup', 'coming soon', 'placeholder'],
    summary: 'Not built yet — placeholder page.',
    body: `
A placeholder. Keep tracking configuration wherever you do today.

Not to be confused with the admin **Configuration** screens, which are org-wide settings. This one is
per project: the register of Aumentum configuration items for a customer — what has to be configured,
who owns it, where each item stands — against the schedule's Configuration workstream.
`,
    related: ['implementation-roadmap', 'bpa'],
  },
  {
    id: 'ticket-status',
    title: 'TS Ticket Dashboards',
    category: 'Project implementation',
    audience: 'all',
    routes: ['/tickets'],
    keywords: ['tickets', 'teamsupport', 'ts', 'support', 'dashboard', 'dashboards', 'charleston', 'search', 'author'],
    summary: 'One place for links to the ticket dashboards, searchable by customer or author.',
    body: `
A launcher, not a dashboard of ours. Each card opens that customer's TeamSupport ticket dashboard on
their external SharePoint site, in a new tab. You need access to that site — if the link lands you on
a permission request, that's SharePoint, not this app.

**Why it opens a new tab instead of showing here.** SharePoint refuses to be displayed inside another
site, so we can't frame it. Showing it in-page means our API fetching the dashboard file and serving
it itself, which needs a tenant permission we don't have yet. Say so if it's worth chasing.

Use the search box to filter by customer or author. Admins add, edit and remove dashboards right on this page (Add dashboard, then Edit or Remove on a card) and can record an Author — who built the dashboard, so you know whom to ask if its data looks wrong. Everyone else sees the list read-only; the author shows on each card.
`,
    related: ['implementation-roadmap'],
  },
  {
    id: 'data-conversion',
    title: 'Data Conversion',
    category: 'Project implementation',
    audience: 'all',
    routes: ['/conversion'],
    keywords: ['data conversion', 'etl', 'legacy data', 'conversion streams', 'conversion issues'],
    summary: "Tracks a project's data migration — streams, deliveries, and issues found along the way.",
    body: `
Replaces the Conversion Issue Tracking spreadsheet and the per-project conversion plan: what's being
converted, which delivery it's on, and the issues found along the way.

**Shipped 2026-09-09 as a prototype** — the team is testing it together before it's polished, so a
few things don't work from this screen yet: you can't add a new stream or log a new issue here (both
need to be set up behind the scenes for now — ask engineering), and a delivery's status can't be
changed here either, so track that elsewhere until it's fixed.

Four tabs, once a project has a stream set up: **Streams** (the migration itself — source system,
target product, treatment), **Deliveries** (each conversion pass in order, dated from Project
Schedule where a matching task exists), **Matrix** (which delivery each software module went
through), and **Issues** (data problems found, filterable by category and severity — "Open — not
ours" means the issue belongs to the customer or a third party, not your own team).
`,
    related: ['implementation-roadmap'],
  },
  {
    id: 'solution-testing',
    title: 'Solution Testing',
    category: 'Project implementation',
    audience: 'all',
    routes: ['/testing'],
    keywords: [
      'testing', 'uat', 'test scripts', 'playwright', 'ptb', 'automated test', 'regression',
      'scenario', 'e2e', 'run', 'schedule', 'recorder agent', 'record', 'environment', 'lists',
      'county', 'release version', 'module', 'stop at first failure',
      'history', 'version', 'restore', 'what changed', 'compare', 'diff',
    ],
    summary: 'Build, run and schedule automated browser tests against a county environment.',
    body: `
Solution Testing (PTB, the Playwright Test Builder) runs automated browser tests against a county's
Aumentum environment, so no one has to click through a workflow by hand each release.

## Getting access
You need to be on the **Access** list. Anyone already on it can add you from its **Access** tab. If the
page says you're not authorized, click **Request access** (add a note if you like). Your request shows up
on the **Access** tab for the team, with a count on the tab, and anyone there can **Approve** or **Deny**
it. Once approved, reopen the page and you're in.

If you're denied, you can **Request again** 24 hours later. A denial can also be undone from **Show
recently decided** on the **Access** tab, and adding someone directly settles their open request.

## The building blocks
- **Test** — one workflow, e.g. a tax detail search.
- **Scenario / E2E** — an ordered list of tests run one after another, built in **Builder**.

## Making a test
Open **Create / Edit Test**, pick the **Module** and **Release Version** (and a **County** if it only applies
to one), then **Generate with AI** from a plain-language description, **Record** yourself doing it,
**Import** a file, or **Write** it. Always review the script before saving.

**Record** and **Show browser** need the **Recorder Agent** on your own computer. Get your key and install
command from **Access → Generate Recorder Agent Key**. Your Aumentum password stays on your machine.

## Running and scheduling
- **Run & Results** — pick an item and an environment, then **Run**. Watch each step and its screenshot live.
  On a failed step, **→ Edit** opens the script at that line and **✦ Fix** asks AI for a repair to review.
- A run is **Passed** only if every test in it passed.
- **Stop at first failure** (Builder) — when on, a failure skips the rest of the sequence.
- **Scheduling** — run something once later, or on chosen weekdays at a set time.

## Script history
Every saved change to a test's script is kept. In **Create / Edit Test**, **History** lists each version (who, when,
how it was made, the optional **What changed?** note, and ✓/✗ from its runs). Compare any version with the editor
or with the last passing version, or **Restore** one to the editor to review and save. In **Run & Results**, a run's
step log shows the version it ran. Click it to see what changed since the test last passed.

A saved **recording** shows up as two versions when the AI tidied it: first the raw recording (**Recorded**),
then the cleaned-up script (**AI refine**), so you can see exactly what the AI changed.

## Export and import
In the **Test Library**, tick one or more Tests (Scenarios and E2Es have no script of their own, so they can't be
ticked). **Export** downloads one test as a \`.spec.ts\` file, or several as one \`.zip\`.

**Import** does one of two things, depending on what's ticked:
- **One test ticked: Import → Update Selected.** The file replaces that test's script, saved as a new version
  in its History ("Imported from…"), so you can compare it or restore the old one. If the file came from a
  different test, you're asked first.
- **Nothing ticked: Import as New.** Pick \`.spec.ts\` files or a \`.zip\`. Each one is listed for review, with name,
  module, county and release filled in from the file when it was exported from PTB. Adjust them, then **Create**.
  A name that's already taken gets "(copy)".

With several tests ticked, Import is off. Tick one to update it, or none to import new tests.

## Lists
The Module, County and Release Version choices live on the **Lists** tab. Anyone with access can add,
rename, reorder (**↑ ↓**) or retire them. A rename updates every test that uses the value.

If runs sit waiting and then fail with "No runner claimed this job", the test runner machine is offline.
The **Dashboard** shows its status.
`,
    related: ['implementation-roadmap'],
  },
  {
    id: 'training-tracker',
    title: 'Training',
    category: 'Project implementation',
    audience: 'all',
    routes: ['/training'],
    keywords: ['training', 'client training', 'sessions', 'attendance', 'coming soon', 'placeholder'],
    summary: 'Not built yet — placeholder page.',
    body: `
A placeholder. Training plans, session calendars and attendance are still tracked outside the app.

When it's built it'll cover the Client Training workstream: what's scheduled, who attended, and what's
outstanding before go-live.
`,
    related: ['implementation-roadmap'],
  },
  {
    id: 'golive-readiness',
    title: 'Go-Live Readiness',
    category: 'Project implementation',
    audience: 'all',
    routes: ['/golive'],
    keywords: ['go-live', 'readiness', 'cutover', 'checklist', 'coming soon', 'placeholder'],
    summary: 'Not built yet — placeholder page.',
    body: `
A placeholder. Cutover plans and readiness checklists still live outside the app.

When it's built it'll be the go/no-go view per project — readiness criteria, sign-offs, and the cutover
sequence — fed by the schedule rather than a separate spreadsheet.
`,
    related: ['implementation-roadmap'],
  },
  {
    id: 'implementation-roadmap',
    title: 'What\'s built and what isn\'t',
    category: 'Getting started',
    audience: 'all',
    routes: [],
    keywords: ['roadmap', 'coming soon', 'not built', 'placeholder', 'status', 'phases'],
    summary: 'An honest map of which screens hold real data today.',
    body: `
**Real, with live data:** My Time, Resource Planning, Time Reports, SOW Intake, Project Setup, Project
Schedule, Portfolio Timeline, Business Process Analysis, and the administration screens.

**Real, but a prototype with known rough edges:** Data Conversion — shipped 2026-09-09 for the team
to test as a group. See its own help topic for what doesn't work yet.

**Real, a separate app shown inside Pulse:** Solution Testing — build, run and schedule automated
browser tests. See its own help topic.

**Sample data only:** Project Dashboard. It says so on the page — it shows the intended layout, and
the numbers are illustrative.

**Placeholders:** Config Tracker, Training, Go-Live Readiness. Nothing is stored in
them.

**Links out, doesn't store anything:** TS Ticket Dashboards — it opens the customer's TeamSupport
dashboard on their SharePoint site.

**Not in the app at all, by decision:** change control (tracked outside this app) and separate status
reporting (it'll be part of the Portfolio Dashboard rather than its own screen).

Financials and budget are the next substantial pieces, followed by the live Portfolio Dashboard.
`,
    related: ['dashboard', 'data-conversion', 'peter'],
  },

  // ---------- Administration ----------
  {
    id: 'resource-configuration',
    title: 'Resource Configuration',
    category: 'Administration',
    audience: 'admin',
    // The landing topic for /administration: People & Time is its first tab, so this is what opens
    // automatically there. Project Configuration / AI Settings / Customer Access are its other tabs --
    // see each one's own topic below, which has no `routes` of its own for the same reason
    // risk-register has none (it's a tab, not its own page).
    routes: ['/administration'],
    keywords: ['configuration', 'admin', 'teams', 'people', 'task types', 'timesheet tasks', 'fiscal calendar', 'capacity factor'],
    summary: 'People, teams, timesheet tasks and the fiscal calendar. Administrators only.',
    body: `
Reached from the **Admin** link in the top-right header, as the "People & Time" tab of the
Administration hub — Project Configuration, Customer Access and AI Settings are its other tabs,
and Maintenance Mode/Login History are their own separate pages, linked from that hub's corner.

Every tab uses the same convention: edit as many rows as you like, then **Save changes** once at the
top. Rows save independently — one failure leaves that row dirty for a retry instead of losing the
rest.

## Timesheet Tasks
The task list people choose from on their timesheet. Each task has a **billable category** and a
**comment policy** (whether a note is required, optional, or not offered). Inactive tasks are hidden by
default — turn them on to edit one back into service. Retire a task rather than deleting it if anyone
has ever logged to it.

## Teams
The team list. Teams drive the report filters and the capacity roll-ups.

## People
One row per person: team, manager, FTE fraction, capacity factor, active flag, and the **Admin**
checkbox.

- **Manager** is what makes someone a manager — set it and they get approvals for that person.
- **FTE fraction** is employment status (0.5 = half-time).
- **Capacity factor** is how much of that FTE is billable — a full-time manager who is 75% billable is
  1.0 FTE with a 0.75 capacity factor. Both feed the capacity maths.

The system won't let you remove the last administrator: there's no self-service way back in.

## Fiscal Calendar
Fiscal weeks and periods. **Generate more weeks** extends the calendar forward; **Add a week** handles a
one-off. Archiving a period **closes it to time entry** — everyone's grid grays those days out — so archive
only after the period is reported.
`,
    related: ['capacity-math', 'fiscal-calendar', 'project-configuration', 'ai-settings', 'customer-access', 'roles-and-permissions'],
  },
  {
    id: 'config-timesheet-tasks',
    title: 'Timesheet Tasks tab',
    category: 'Administration',
    audience: 'admin',
    parent: 'resource-configuration',
    routes: [],
    keywords: ['task type', 'timesheet task', 'comment policy', 'billed to', 'requires account', 'seed'],
    summary: 'The task list people log against, and the four settings on each one.',
    body: `
One row per task. Four settings decide how it behaves:

- **Billable category** — Billable, Non-Billable Productive, Non-Billable, or Time Off. Drives every
  utilization number.
- **Billed to** — Project, Market, Core Product, or Administrative. A *separate* axis from category:
  category says whether it's chargeable, this says whose budget it lands in. It's what the Hours by
  Category panels group on.
- **Comment policy** — *Not Available*, *Available*, or *Required*. Required blocks submitting the week
  until the note is there; that's the point for broad buckets like "Other (Non-Billable)".
- **Requires customer** — for administrative work done for a specific customer (Travel, Post Go Live
  Support). The row then asks for a customer instead of a project.

Inactive tasks are hidden by default; show them to edit one back into service. **Retire rather than
delete** anything people have logged against.

An empty task dropdown on someone's timesheet usually means the standard set was never seeded in this
environment — there's a seed action on this tab.
`,
    related: ['resource-configuration', 'billable-categories', 'administrative-time'],
  },
  {
    id: 'config-teams',
    title: 'Teams tab',
    category: 'Administration',
    audience: 'admin',
    parent: 'resource-configuration',
    routes: [],
    keywords: ['team', 'add team', 'org'],
    summary: 'The team list, and what depends on it.',
    body: `
Add and rename teams here. Teams aren't only labels — they drive the team filters on every report, the
Capacity roll-ups, and the Project → Team → Person grouping on the forecast grid.

A person belongs to one team, set on the **People** tab. Renaming a team carries everywhere immediately;
there's no re-tagging to do.
`,
    related: ['resource-configuration', 'config-people', 'capacity-view'],
  },
  {
    id: 'config-people',
    title: 'People tab',
    category: 'Administration',
    audience: 'admin',
    parent: 'resource-configuration',
    routes: [],
    keywords: ['people', 'user', 'manager', 'fte', 'capacity', 'admin', 'active', 'leaver', 'start date', 'end date', 'ramp', 'onboarding', 'new hire'],
    summary: 'The settings that decide someone\'s capacity, their approver, and their access.',
    body: `
User records are created automatically on first sign-in, defaulting to the read-only **Viewer**
role; this tab is where they're set up and promoted.

- **Role** — Viewer (read-only, the default), Member, or Admin. Promote a new hire here once their
  access is confirmed — see *Who can do what* for what each unlocks.
- **Team** and **Manager** — the manager field is what makes someone a manager: set it and they start
  receiving that person's timesheets for approval. Someone on **no team** has no capacity at all,
  because the monthly-hours standard lives on the team.
- **FTE fraction** — employment status (0.5 = half-time).
- **Capacity** — how much of that FTE is available for billable delivery, as a **percentage**. A
  full-time manager who is 75% billable is FTE 1.0, Capacity 75%. Unset counts as 100%.
- **Start** / **End** — the dates the person was employed. Capacity counts only in months they were
  here for the **whole month**, so someone starting on the 15th begins the following month. Leave
  both blank for established staff — blank start means "has always been here", blank end "still here".
- **Ramp** — months where capacity differs from the usual, e.g. a new hire who isn't fully billable
  yet. See below.
- **Admin** — grants the administration screens. The system refuses to remove the last administrator;
  there's no self-service way back in. The role dropdown also offers **Viewer** (read-only) and
  **R&D** — read-only everywhere except **R&D Time Entry**, which it can edit. Use R&D for someone who
  only keys R&D hours and shouldn't touch forecasts or anything else.
- **Active** — untick for a leaver. **Set their End date too**: on its own, unticking Active removes
  them from capacity in past months as well, while their logged hours stay in the reports.

Edit as many rows as you like, then **Save changes** once. Rows save independently, so a single failure
doesn't cost you the rest.

### The Ramp button

Opens a per-month capacity editor for that person. Only months that **differ** from their usual
Capacity % need a row — every other month uses the grid value, so most people have none.

**Set a run of months** writes several at once: pick the first month and a list of percentages.
It works in either direction — \`40, 70, 100\` ramps a new hire up over three months, and
\`50, 50, 25\` covers someone who was temporarily more billable than usual. Months after the last one
fall back to their usual capacity automatically, so you never need a trailing 0%.

A month's Capacity % **replaces** the grid value rather than scaling it, including a grid value of 0 —
so a normally non-billable person can be given a billable month. Leave **FTE %** blank: ramping up or
down doesn't change someone's employment status. Rows outside the Start/End dates are ignored.

Save any pending grid edits first — the Ramp button is disabled while the grid has unsaved changes,
because opening it reloads the grid.
`,
    related: ['resource-configuration', 'capacity-math', 'roles-and-permissions'],
  },
  {
    id: 'config-fiscal-calendar',
    title: 'Fiscal Calendar tab',
    category: 'Administration',
    audience: 'admin',
    parent: 'resource-configuration',
    routes: [],
    keywords: ['fiscal calendar', 'generate weeks', 'archive', 'close period', 'reopen'],
    summary: 'Extending the calendar, and closing a period to time entry.',
    body: `
The fiscal weeks and periods everything reports against.

- **Generate more weeks** extends the calendar forward in bulk — do this before a fiscal year starts, or
  people will find weeks they can't log to.
- **Add a week** handles a one-off correction.
- **Archiving a period closes it to time entry.** Those days gray out on everyone's timesheet and no
  hours can be added or changed; reports still read them. Archive only once the period is reported, and
  expect a request to reopen when someone finds a missed day.

Nothing else references a fiscal week by key, so a correction here doesn't orphan data — reporting
joins by date.
`,
    related: ['resource-configuration', 'fiscal-calendar', 'accounting-export'],
  },
  {
    id: 'project-configuration',
    title: 'Project Configuration',
    category: 'Administration',
    audience: 'admin',
    // No `routes`: it's the "Projects" tab of /administration, and that route opens the
    // resource-configuration topic, which links here -- same convention as risk-register.

    keywords: [
      'templates', 'phases', 'workstreams', 'projects', 'accounts', 'placeholder', 'merge',
      'project type', 'poc', 't&m', 'internal', 'schedule template', 'modules',
    ],
    summary: 'Project templates, project/account records, and the module taxonomy. Administrators only.',
    body: `
Three tabs.

## Project Templates
The phase and workstream skeleton new projects are built from. Edit the phases, then the workstreams
within each — this is the methodology, so a change here shapes every project created afterwards, not
existing ones.

## Projects & Accounts
The project and account records: name, account, status, PM, project type and schedule template.

- **The PM field is what grants project-manager rights** — approvals for time logged to that project,
  and editing its forecast.
- **Project type** — *POC*, *T&M* or *Internal*. It decides how the engagement is billed, and T&M
  projects are left out of the Project Schedule picker.
- **Placeholder projects** let you forecast work that hasn't been sold yet. The common way one gets
  taken over is a PM running **Load SOW** against it from its own Project Setup page, not a manual
  merge here — see *Placeholder projects*. **Merge**, on this tab, is for the separate case of folding
  one already-real project into another.
- **Archived** projects keep their history and stay in reports, but take no new forecast.

## Modules
The module taxonomy BPA scopes projects against — also what feeds Project Setup's Software Modules In
Scope tree and Assign Team's module-lead pickers. See *Modules tab*.
`,
    related: ['resource-configuration', 'resource-planning', 'project-setup', 'project-modules-config'],
  },
  {
    id: 'project-templates',
    title: 'Project Templates tab',
    category: 'Administration',
    audience: 'admin',
    parent: 'project-configuration',
    routes: [],
    keywords: [
      'template', 'phases', 'workstreams', 'methodology', 'default template',
      'migration', 'upgrade', 'microsoft project', 'mpp',
    ],
    summary: 'The phase and workstream skeleton new project schedules are built from.',
    body: `
A template is the methodology in editable form: its **phases** in sequence, and the **workstreams** within
each. Editing one shapes every project created from it *afterwards* — existing projects keep the
schedule they were built with, so this is never a way to reshape live work.

**Which templates exist**

- **Aumentum Standard Methodology** — the default, seeded from the standard phase list.
- **Migration (Upgrade)** — the Sedgwick County plan, imported from Microsoft Project. Use it for
  upgrade/migration work. It carries the full task-and-activity detail a hand-built template doesn't:
  six phases, 125 rows, and the dependency links between them.
- **Net New (Conversion)** — the Clark County plan, likewise imported. Use it for a net-new
  implementation rather than an upgrade of an existing system: seven phases, 105 rows and 69 links,
  built around repeated conversion and configuration iterations.

Both imported templates include the conditional **Software Development** phase, holding the R&D delivery
groups. Drop that phase when a project has no custom development scope.

Templates can be built by hand here, or imported from a Microsoft Project plan by an engineer — a plan
has to be saved as **XML Format** first, since .mpp files can't be read on the server. Ask Jeremy.

An imported template carries its source plan's quirks with it, since importing reports oddities rather
than silently fixing them. Both of the above have known ones — a mis-numbered delivery group, a phase
closeout dated before its own phase's work ends, some spacing typos. Worth a read-through before the
first project is built from one.

Two things to know before editing:

- A phase you add can't be inferred from SOW wording during extraction — the classifier works from the
  standard vocabulary. A new phase only gets used if a milestone lands there some other way.
- The Gantt derives its pre-project/post-project bookends from phase titles it recognises, so a phase
  with an unfamiliar title reads as having no stage.

⚠️ This area was shipped knowingly incomplete — worth a check with Jeremy before relying on a custom
template for a live project.
`,
    related: ['project-configuration', 'phases-and-workstreams', 'populate-from-sow'],
  },
  {
    id: 'projects-and-accounts',
    title: 'Projects & Accounts tab',
    category: 'Administration',
    audience: 'admin',
    parent: 'project-configuration',
    routes: [],
    keywords: [
      'project', 'account', 'customer', 'pm', 'lifecycle', 'archive', 'merge', 'placeholder',
      'project type', 'poc', 't&m', 'internal', 'schedule template',
    ],
    summary: 'Project and customer records, the PM assignment, project type, and merging placeholders.',
    body: `
One row per project, plus the customer accounts they belong to. Every column sorts.

- **PM** is not just a label — it grants project-manager rights: approving time logged to that project
  and editing its forecast.
- **Status** is the PM-set stage: *Placeholder, Startup, Active, Final Acceptance, Converted, Completed,
  Archived*. It's separate from the setup status Project Setup tracks (that one is about folders and
  provisioning). **Archived** keeps history in reports but takes no new forecast, and archived projects
  are hidden by default in the Project Schedule picker.
- **Schedule template** is the methodology the *next* population of that project's schedule builds from.
  Changing it doesn't touch a schedule that already exists.
- **Add a placeholder project** for work that isn't signed yet. When the SOW lands, the normal path is
  **Load SOW** from that placeholder's own Project Setup page, not this tab — see *Placeholder
  projects*. **Merge**, here, is for consolidating two project rows that are both already real.

## Project type
How the engagement is contracted. Unlike status, it doesn't change as the project progresses.

- **POC** — percentage-of-completion, i.e. fixed fee. This is the default.
- **T&M** — time and materials, billed hourly. These are **left out of the Project Schedule picker**,
  because hourly work is billed against hours rather than against a plan.
- **Internal** — non-billable.

Note that the **Internal** *account* is a separate thing from the Internal *type*: projects on that
account are excluded from the accounting and hours reports by design, whatever their type.
`,
    related: ['project-configuration', 'project-lifecycle', 'placeholder-projects'],
  },
  {
    id: 'project-modules-config',
    title: 'Modules tab',
    category: 'Administration',
    audience: 'admin',
    parent: 'project-configuration',
    routes: [],
    keywords: [
      'module', 'modules', 'taxonomy', 'software modules in scope', 'bpa', 'parent module',
      'retire', 'active', 'sequence',
    ],
    summary: 'The module taxonomy — one list feeding BPA, Software Modules In Scope, and Assign Team.',
    body: `
One flat, sortable grid: **Name**, **Code**, **Parent** (optional — building a hierarchy like Tax →
Real Property), **Sequence** (display order; ties break alphabetically), and **Active**.

This is the same taxonomy BPA scopes projects against — it isn't a separate list for Project Setup.
Add a module here and it shows up immediately in Project Details' Software Modules In Scope tree and
in BPA's own module tree.

**Retire, don't delete.** Unchecking Active removes a module from every picker without touching
projects that already reference it — deleting is refused outright once a module has children, BPA
library topics, project scope records or sessions attached, which in practice is almost always.
`,
    related: ['project-configuration', 'project-setup', 'bpa'],
  },
  {
    id: 'maintenance-mode',
    title: 'Maintenance Mode',
    category: 'Administration',
    audience: 'admin',
    routes: ['/maintenance'],
    keywords: ['maintenance', 'lock out', 'downtime', 'banner'],
    summary: 'Lock everyone but administrators out of the app. Administrators only.',
    body: `
Turn it on and every non-administrator sees a "down for maintenance" message with whatever text you
set, instead of the app. Administrators keep full access and see a banner across the top so it's
obvious the site is closed.

Use it for a data migration or anything where half-saved work would be a problem. Set a message that
says when you expect to be back — it's the only thing users will see. Remember to turn it off.
`,
    related: ['resource-configuration', 'bpa-library'],
  },
  {
    id: 'login-history',
    title: 'Login History',
    category: 'Administration',
    audience: 'admin',
    routes: ['/login-history'],
    keywords: ['login', 'audit', 'sessions', 'who signed in', 'history'],
    summary: 'Who signed in and when. Administrators only.',
    body: `
One row per visit, newest first, sortable and paginated — sorting is applied across the whole history,
not just the page you're looking at. **Signed in** is the first request of that visit, **Last active** the
most recent, and Duration the gap between them.

## A visit, not a token
A visit stays one row across a break of up to two hours. That's deliberate: the browser quietly renews
its access token about once an hour, and counting those renewals as sign-ins made one tab left open
overnight look like a login every hour. Come back after longer than two hours and you get a new row.

Two things to know before reading Duration closely. **Last active** is only accurate to a few minutes,
and it can run up to two hours past the point someone actually stopped working — that tail is an idle
tab sitting open until the app signs it out.

It answers "has this person been in the app, and when" — adoption, and access questions. It is
deliberately **not** a change log: it doesn't record what anyone did or edited. Old rows are purged on a
retention schedule.
`,
    related: ['roles-and-permissions'],
  },
  {
    id: 'project-budgets',
    title: 'Project Budgets',
    category: 'Project management',
    audience: 'manager',
    routes: ['/project-budgets'],
    keywords: ['budget', 'plan vs actual', 'forecast', 'revenue', 'recognized', 'billed', 'burn', 'hours by period', 'go-live', 'milestones', 'project level', 'team filter'],
    summary: 'Budget vs. actual vs. forecast by team, with a month-by-month chart of hours, billing and recognized revenue.',
    body: `
Pick a project to see its budget lines, the two summary cards, the **Plan vs. Actual vs. Forecast**
table and the **Hours by Period** chart.

## Reading the colors
**Color says what is measured, the fill says whether it has happened.** Hours are light blue and
revenue is green; billed revenue is violet. **Solid** means actual and **striped** (or a dashed line)
means forecast or scheduled. Red is kept for warnings only.

## Summary cards
Hours and Revenue each show Planned, Actual, Forecast and Remaining, plus one bar: actual solid, forecast
striped, with each as a percentage of budget. If actual plus forecast runs past the budget the bar
stretches and a dark tick marks where the budget ends.

## Plan vs. Actual vs. Forecast
One row per team, plus **Project level** (budget lines with no team — budget only, since hours can't be
attributed to it), **R&D** and **No team** where they apply. Actuals are billable timesheet hours through
last month; forecast is the resource plan from this month on.
- **Team filter** is multi-select. **Project level** is one of its options. With a selection, the
  **Total** row adds up only the rows shown.
- **Revenue** is hours priced at the billing rate **in effect for each period** — a mid-project rate
  change only affects the months after it. A manual hours adjustment has no date, so it is priced at
  today's rate. See **Billing Rates**.
- **Export to Excel** follows the filter.

## Hours by Period
Monthly hours on a calendar axis (years and quarters across the top).
- **Columns** are hours: solid for actual months, striped for forecast.
- **Violet diamonds** are billable milestones — any milestone with an amount — with the dollars under each.
  Filled means done; hollow means still to come. Hover for the milestone names.
- **Green line** is cumulative revenue recognized (hours × rate); **violet line** is cumulative revenue
  billed. Both use the dollar axis on the right. Dashed parts are forecast or scheduled.
- **Red months** are where recognized revenue is ahead of billed — work done but not yet invoiced. The
  line under the chart gives the gap as of last month.
- **Orange line** is today; the **red dashed line** is the Go-Live milestone.

Billed uses each milestone's actual date once it is done and its target date until then, so future
points show scheduled billing, not invoices already sent.
`,
    related: ['billing-rates'],
  },
  {
    id: 'billing-rates',
    title: 'Billing Rates',
    category: 'Project management',
    audience: 'manager',
    routes: ['/billing-rates'],
    keywords: ['billing rate', 'rate', 'bill rate', 'license rate', 'lf rate', 'services rate', 'effective date', 'revenue', 'finance', 'wip'],
    summary: "Every project's Services and License hourly rates over time, in one place.",
    body: `
Open it from **Bill Rates and History →** on Project Budgets (or from Administration). The grid lists
every project with the Services and License rate in effect today and the date each took effect.
Sort by any column; **Missing Services rate** and **Missing License rate** narrow it to the gaps, and
**Show archived** brings back archived projects (some still carry 2026 hours that need pricing).

## Two rates per project
- **Services** — the labor rate in dollars per hour. Project Budgets uses it to price planned and
  forecast hours, and Revenue reporting multiplies hours by it.
- **License** — the SW License run rate: the license contract dollars divided by total forecast hours,
  the "License Only" rate in Finance's WIP workbook.

## Changing a rate
Click **History / edit** on a project to see every rate it has had, newest first. A **current** badge
marks the rate in effect today and **scheduled** marks one that starts later. To change a rate, pick the
kind, enter the new rate and the date it takes effect, and **Add rate**. You can also do this for
Services from **Change** on Project Budgets, which now asks for an effective date.

A change is always a **new dated row** — the old rate stays on record, so earlier months keep the rate
they were priced at. Back-date the effective date to the first of the month the rate really applied from.

## Fixing a mistake
Because rows are never edited in place, a wrong entry is corrected by an **administrator deleting it**
(Delete, then confirm) and adding the right one. Don't delete a rate that was simply replaced by a
later one — that rewrites history.
`,
    related: ['project-setup'],
  },
  {
    id: 'ai-settings',
    title: 'AI Settings',
    category: 'Administration',
    audience: 'admin',
    // No `routes`: it's the "AI Settings" tab of /administration, and that route opens the
    // resource-configuration topic, which links here -- same convention as risk-register.

    keywords: ['ai', 'prompt', 'model', 'claude', 'extraction', 'sow', 'peter', 'usage', 'cost', 'tokens', 'limits'],
    summary: "Peter's usage and limits, plus the prompt behind SOW extraction. Administrators only.",
    body: `
Three tabs.

**Usage** — how many questions Peter has answered, by person, with input/output tokens and an
estimated dollar cost at that model's published rate. Some rows predate token tracking and show a
call count with no cost figure — that's expected, not missing data. The estimate isn't your invoice:
for the billed figure, check Azure Cost Management (Cost analysis, filtered to the Foundry resource).

**Limits** — override Peter's daily question cap and which model he runs on, without a deployment.
Leave a field blank to fall back to the environment default; "Currently in effect" always shows
what's actually running, whichever source it came from.

**SOW Extract Prompt** — read-only view of the prompt that's actually running for SOW extraction,
plus the model and provider in use. It reads the same file the extraction itself reads, so it can't
drift from reality. **Edited in the repository, not here** — on purpose: a prompt edited live in a
GUI is what we left behind, with no diff, no review, and no way to tell which version produced a
given historical extraction. Peter has his own separate prompt, not shown on this page.
`,
    related: ['sow-intake', 'peter'],
  },
  {
    id: 'bpa-library',
    title: 'Editing the BPA topic library',
    category: 'Administration',
    audience: 'admin',
    routes: [],
    keywords: ['bpa library', 'topics', 'standard library', 'sync from code', 'national', 'market'],
    summary: 'The standard BPA questions every project is seeded from. Administrators only.',
    body: `
To open it you need to be an administrator **and have maintenance mode on** — editing the library while
BPAs are being run is exactly the race this avoids. Then the **BPA Library** entry becomes available.

Pick a module and you get its topics; each one's title, prompts and discussion rows are editable.

- **Retire, don't delete.** Untick *Active* to stop a topic being offered to new projects. Existing
  projects keep theirs.
- **National vs Market.** Every topic is one or the other — national topics apply everywhere, market
  topics only to the relevant market.
- **Sync from code** loads the engineer-maintained version of the library. It only ever adds; it won't
  overwrite what you've edited here.

Customers can't see any of this today.
`,
    related: ['bpa', 'maintenance-mode'],
  },

  // ---------- How things work ----------
  {
    id: 'fiscal-calendar',
    title: 'Fiscal weeks, periods and closed periods',
    category: 'How things work',
    audience: 'all',
    routes: [],
    keywords: ['fiscal', 'period', 'week', 'month', 'closed', 'archived', 'FY26'],
    summary: 'Why a timesheet week can land in two months, and what "archived" means.',
    body: `
Time is reported against a **fiscal calendar**, not the calendar month. Fiscal periods are named like
\`FY26-M07\` and are made of fiscal weeks.

The timesheet week is **Saturday to Friday**, which doesn't line up with fiscal month boundaries. When a
week straddles two periods, My Time flags it above the dates and its hours are reported under both —
nothing is lost or double-counted, but "this week's total" and "this month's total" won't reconcile
straight across.

**Archived** period = closed to time entry. Those days gray out on everyone's timesheet and no hours can
be added or changed. Reports still read them. Only an administrator can archive or reopen a period.
`,
    related: ['my-time', 'time-reports', 'resource-configuration'],
  },
  {
    id: 'billable-categories',
    title: 'Billable categories and utilization',
    category: 'How things work',
    audience: 'all',
    routes: [],
    keywords: ['billable', 'non-billable', 'productive', 'utilization', 'category', 'time off'],
    summary: 'The four categories, and how the two utilization percentages are calculated.',
    body: `
Every timesheet task carries one of four categories — you don't pick it, the task does:

- **Billable** — chargeable customer work.
- **Non-Billable Productive** — real work that isn't chargeable (presales, internal projects, enablement).
- **Non-Billable** — everything else (admin, general meetings).
- **Time Off** — leave.

## Utilization
Both percentages on My Time's recap use **hours you logged that week, excluding time off**, as the
denominator:

- **Billable utilization** = billable hours ÷ that total.
- **Productive utilization** = billable + non-billable productive ÷ that total.

Because time off is out of both sides, a short week doesn't dilute your percentages — a three-day week
with all billable time still reads 100%.
`,
    related: ['my-time', 'time-reports', 'capacity-math'],
  },
  {
    id: 'time-off',
    title: 'Logging time off',
    category: 'How things work',
    audience: 'all',
    flag: 'resources',
    routes: [],
    keywords: ['pto', 'vacation', 'leave', 'holiday', 'sick', 'time off'],
    summary: 'Log leave on the timesheet like anything else — it just sits outside utilization.',
    body: `
Add the relevant time-off task to your timesheet the same way as any other row and enter the hours.
It's non-billable, and it's excluded from both sides of the utilization calculation, so leave never
counts against your percentages.

This is a record of hours, not a leave-request system — it doesn't approve or track your balance.
Request leave wherever you do today, then log it here.
`,
    related: ['my-time', 'billable-categories'],
  },
  {
    id: 'capacity-math',
    title: 'How capacity is worked out',
    category: 'How things work',
    audience: 'all',
    flag: 'resources',
    routes: [],
    keywords: ['capacity', 'fte', 'capacity factor', 'available hours', 'over-allocated', 'start date', 'ramp', 'monthly capacity', 'new hire'],
    summary: 'Team standard hours × FTE × billable capacity, for the months someone was actually here.',
    body: `
Capacity for one person in one month is:

**Team standard monthly hours × FTE fraction × Capacity %**

- **Team standard monthly hours** — set per team (e.g. 150). Someone on **no team** has no capacity,
  because there's no standard to multiply.
- **FTE fraction** — employment status. 1.0 full-time, 0.5 half-time.
- **Capacity %** — how much of that FTE is available for billable delivery. A full-time manager who is
  75% billable is FTE 1.0, Capacity 75%. Unset counts as 100%.

Keeping FTE and Capacity apart means you can change someone's hours without restating how billable
they are, and vice versa. An unset Capacity is treated as 100% rather than blocking the calculation.

### It varies by month

Capacity isn't one figure for all time:

- **Outside someone's Start/End dates they have no capacity at all** and drop out of the calculation
  entirely — they aren't counted as a zero, which would drag an average down. A month counts only if
  they were employed for the **whole** month, so a mid-month start begins the following month.
- **Any month can be overridden** from the Ramp button on People — an onboarding ramp, leave, or a
  spell more or less billable than usual. An override replaces the Capacity % for that month only;
  months either side use the usual value.

Where a person genuinely has no capacity on file — no team standard, or no FTE — they're left out of
averages rather than counted as zero. Reports that do this say how many were excluded.

Capacity is compared against forecast hours on Resource Planning: **green under 90%**, **yellow 90–110%**,
**red over 110%**, gray where there's no capacity on file. It's also the denominator for the two
utilization rates on Hours by Category.
`,
    related: ['resource-planning', 'resource-configuration', 'config-people', 'hours-by-category', 'billable-categories'],
  },
  {
    id: 'fcst-vs-actuals',
    title: 'FCST, baselines and actuals',
    category: 'How things work',
    audience: 'all',
    flag: 'resources',
    routes: [],
    keywords: ['fcst', 'forecast', 'baseline', 'actuals', 'variance', 'plan'],
    summary: 'Three different numbers that all look like "the plan".',
    body: `
- **FCST (forecast)** — the current plan: hours you expect to spend, by person, project and month. It
  changes whenever the plan changes.
- **Baseline** — a locked snapshot of the forecast at a point in time (annual, plus optional quarterly
  re-locks). It never changes; that's the point. Variance is measured against it.
- **Actuals** — hours actually logged on timesheets.

So: *FCST vs Actuals* asks "are we burning what we said we would". *Baseline variance* asks "is the plan
still the plan we committed to". A project can be on forecast and well off baseline at the same time —
that's the forecast having moved, and it's exactly what the two numbers together are for.
`,
    related: ['resource-planning', 'time-reports'],
  },
  {
    id: 'timesheet-compliance',
    title: 'What "compliance" means on a timesheet',
    category: 'How things work',
    audience: 'all',
    flag: 'resources',
    routes: [],
    keywords: ['compliance', 'submitted', 'in progress', 'missing', 'chasing'],
    summary: 'The statuses on the Compliance report, and how a person\'s status is chosen.',
    body: `
Compliance tracks how far a week got through the process, not whether the hours were right. Five
statuses, in order:

- **Not Started** — nothing logged for that week at all.
- **In Progress** — hours entered, not all submitted.
- **Submitted** — submitted, waiting on your manager.
- **Manager Approved** — your manager has approved it; the project manager hasn't yet.
- **Approved** — both approvals in.

A week only moves up when **every** entry in it clears the gate, so one unsubmitted row holds the
whole week at *In Progress*.

On the report, a person shows the **worst status they have in any week** of the period. That's
deliberate: the report exists to answer "who still needs chasing", and one unsubmitted week out of four
is still a chase.

If yours looks wrong, check every week in the period rather than just the latest one — a week you
half-filled and never submitted is the usual culprit.
`,
    related: ['my-time', 'time-reports', 'approvals'],
  },
  {
    id: 'project-lifecycle',
    title: 'Project status, and project setup status',
    category: 'How things work',
    audience: 'all',
    routes: [],
    keywords: [
      'lifecycle', 'active', 'archived', 'completed', 'startup', 'status', 'setup status',
      'project status', 'converted', 'final acceptance', 'pending',
    ],
    summary: 'Placeholder → Startup → Active → Completed → Archived, and the other "status".',
    body: `
**Project status** is set by hand by the PM in Project Configuration → Projects & Accounts, and says
where the engagement is:

- **Placeholder** — not signed yet; exists so forecasting isn't blocked on paperwork.
- **Startup** — the SOW has landed and the project is being set up. A committed SOW lands here.
- **Active** — in delivery.
- **Final Acceptance** — delivery is done and the customer's sign-off is outstanding.
- **Completed** — delivery finished.
- **Converted** — came across from OnePlan rather than starting life in this app.
- **Archived** — closed. Keeps its history in reports, but takes no new forecast and isn't offered when
  adding people to a plan.

## The other two "statuses"
**Project setup status** — Pending / In Progress / Complete — is a different field, and tracks
provisioning only: folders, the customer site. A new project starts at *Pending*. **RAG status** is
the health snapshot. Neither one is the project status above.

## Not a status at all: project type
**Project type** — *POC*, *T&M*, *Internal* — sits beside status on the same grid, but it's how the work
is contracted rather than a stage it moves through, so it doesn't change as the project progresses. It
does have one visible effect: T&M projects aren't offered in the Project Schedule picker.

Project Setup's first step lists projects at **Placeholder** or **Startup** only — the two stages that
page exists to work through. Once a project is Active it has no setup left to do, so it drops off that
list.
`,
    related: ['projects-and-accounts', 'placeholder-projects', 'team-allocations'],
  },
  {
    id: 'placeholder-projects',
    title: 'Placeholder projects, and what happens when the SOW arrives',
    category: 'How things work',
    audience: 'all',
    routes: [],
    keywords: ['placeholder', 'merge', 'promote', 'pipeline', 'unsigned', 'forecast ahead'],
    summary: 'How to forecast work that isn\'t signed yet, and how the SOW takes the placeholder over.',
    body: `
A **placeholder project** exists so resourcing isn't blocked on a signature: create it, forecast
against it, staff it. It behaves like a real project on the forecast grid.

## When the SOW is signed
Open the placeholder's own Project Setup page and **Load a SOW** under Placeholder actions — the
system already knows this is the target, so there's no picker to use. On Commit, **that project
becomes the real one**: it takes the SOW's name, customer and start date, stops being a placeholder,
and moves to *Startup*. Nothing is copied anywhere: the forecast, the team plan and the locked
baselines were already attached to it and simply stay attached.

If the placeholder already had data of its own (a name, dates, modules, services) that disagrees with
what the SOW says, a **Resolve differences** step asks which value to keep, field by field — only for
the fields that actually conflict. Everything else merges automatically.

For a SOW with no placeholder to belong to, use **Load SOW →** on the Project Setup list instead —
same flow, but with the old picker: leave it on **Create a new project**, or pick one from the list. If
exactly one placeholder sits on the customer the SOW names it's picked for you; picking one on a
*different* customer is called out and warned about, since committing moves that placeholder, and
everything forecast on it, to this SOW's customer, with no second project to undo from.

## Merging, for the other case
Sometimes the real project already exists on its own — imported, or created by hand — and a
placeholder needs to fold into it. An administrator **merges** it, in Configuration → Projects. The
placeholder's assignments *and* its team-level forecast move across, and the placeholder itself:

- stops being offered anywhere you'd plan new work,
- disappears from the reports' project pickers, since its hours are now the real project's,
- stays in the database rather than being deleted, so the trail of what was planned when survives.

A merge is refused if both projects already forecast the same team in the same month — the two plans
would be for the same work, so one side has to be cleared first rather than the app picking a winner.

So a placeholder you remember and can't find has gone one of two ways: it *became* a project, under
the SOW's name, or it merged into one.
`,
    related: ['projects-and-accounts', 'team-allocations', 'project-lifecycle', 'sow-intake'],
  },
  {
    id: 'administrative-time',
    title: 'Administrative time and internal work',
    category: 'How things work',
    audience: 'all',
    flag: 'resources',
    routes: [],
    keywords: ['administrative', 'pto', 'travel', 'internal meeting', 'post go live support', 'core product', 'market'],
    summary: 'Where non-project hours go, and why some of them still name a customer.',
    body: `
Hours that aren't project delivery go on a single **Administrative Time** project — PTO, internal
meetings, travel, general admin. It isn't a real engagement; it's the bucket that keeps non-project time
out of project numbers.

**Some administrative work still names a customer.** Travel and Post Go Live Support are done *for*
someone, so those rows ask for a **customer** instead of a project, and the customer is what you see as
the row's headline. The hours still sit on Administrative Time underneath.

Separately, every task is billed to one of four buckets — **Project, Market, Core Product,
Administrative** — which is what the Hours by Category panels split on. Core product and market work are
real, non-project work: they're not the same thing as administrative overhead, even though neither is a
customer project.
`,
    related: ['my-time', 'billable-categories', 'hours-by-category', 'config-timesheet-tasks'],
  },
  {
    id: 'peter',
    title: 'Peter the Project Monkey',
    category: 'How things work',
    audience: 'all',
    routes: [],
    keywords: ['peter', 'chatbot', 'assistant', 'monkey', 'ask'],
    summary: 'Ask questions about the Hub in your own words instead of searching.',
    body: `
Peter is an assistant built into this panel. Open Help and choose **Ask Peter** at the bottom, then
type the question the way you'd say it out loud — "how do I unsubmit a week?", "who signs off my
time?". He answers from this help center and links the article he used, so you can read the detail.

## What he can and can't do

He knows how the application works, plus real hours and allocation data he can look up for you: hours
logged on a project (broken down by category), a person's planned allocation for a given month, and
trend/breakdown questions over logged time — by month, task type, project, person, or billing
category, including "what share of total time was this." Ask "how many hours has Ada County logged,
by category?", "what's Dean's allocation in October?", or "is non-billable Innovation time trending
up?" and he'll answer with real numbers — and these aren't limited to your own projects or hours, so
it's fair to ask about anyone or anything in the portfolio.

- **Everything else about your data, he still can't see.** Budgets, RAG status, milestones, SOW
  terms, forecasts — none of that is wired up. Ask him what one of those numbers means and he'll
  explain it; ask him what the number *is* and he'll tell you which screen shows it, same as before.
- **He needs more training and expansion before he can handle complex questions.** The lookups above
  are still a narrow, growing step — he can't yet reason across multiple dimensions at once (a
  month-by-project grid, say) or handle anything that isn't shaped like one of his tools. If he says
  he can't answer something that sounds like it should be easy, that's expected for now, not a bug.
- **He only knows what's written here** for anything outside those two lookups. If a topic isn't in
  this help center, he'll say so rather than guess. That's deliberate — a confident wrong answer about
  a process is worse than no answer.
- **He only sees what you can see** for help-center articles specifically — the same ones this panel
  shows you, so he won't describe a screen your account can't open.

## If he gets it wrong

Tell Jeremy. Every answer is logged with the question that produced it, so a bad one can be traced
and the underlying help article fixed — which fixes it for everyone, not just you.

There's a daily limit on questions per person. It exists to stop a runaway loop, not to ration
you; if you ever actually hit it, that's worth mentioning.
`,
    related: ['ai-settings'],
  },

  // ---------- Administration ----------
  {
    id: 'customer-access',
    title: 'Customer Access',
    category: 'Administration',
    audience: 'admin',
    // No `routes`: it's the "Customer Access" tab of /administration, and that route opens the
    // resource-configuration topic, which links here -- same convention as risk-register.

    keywords: [
      'customer access', 'external user', 'contact', 'portal', 'invite', 'invitation',
      'send invitation', 'send reset link', 'password', 'forgot password', 'lockout', 'locked out',
      'not yet signed up', 'entra', 'guest', 'otp', 'verification code', '2fa', 'two-factor',
      'new device', 'trusted device', 'county', 'group', 'filter', 'delete', 'remove', 'import',
      'spreadsheet', 'template', 'bulk', 'project contact list',
    ],
    summary: 'Who can sign in to the customer portal, which project(s) they see, and how to get them in.',
    body: `
Customer contacts have their own email-and-password sign-in at \`/portal\`, separate from staff
sign-in — this screen is where an administrator manages it.

## Adding a contact
**Add a customer contact** creates the record but sends nothing. Grant the project(s) they should
see first — a contact with no project access can sign in but the portal is empty — then send them
in.

## Getting them signed in
The button next to a contact does one of two things, depending on whether they've set a password
yet:

- **Send invitation** — a single-use link, good for 7 days, to set a first password. Shown while
  the badge reads **Not yet signed up**.
- **Send reset link** — same button, once the badge flips to **Password set**. Good for 2 hours.

Either is safe to click again: a new link invalidates whatever was sent before, so there's never
two live links for one contact. A contact who clicks **Forgot your password?** on the portal
themselves triggers the same thing — an admin doesn't have to be involved unless they want to be.

## Locked out
Five wrong passwords or verification codes locks the account for 15 minutes — they share one
counter — and the portal tells the contact this directly, offering the reset link as a way past it.
Nothing to do here except wait it out or have them use the link — **Disable** is for revoking
access outright, not for clearing a lockout.

## Signing in from a new browser
The portal emails a 6-digit code the first time an account signs in from a browser it doesn't
recognize, on top of the password. That browser is then remembered for 30 days (longer if the
contact keeps using it), so this is a one-time step per device, not a code on every sign-in.
Nothing to configure here — it happens automatically and needs no admin action.

## Project access and disabling
Grant and revoke projects from a contact's **Projects** cell — a contact only ever sees what's
granted here, independent of anything else in the app. **Disable** blocks sign-in immediately;
**Enable** does not resend an invitation, so re-invite separately if their link has since expired.

## Finding someone
Contacts are grouped by county — click a county's header to collapse or expand it, and a contact
with access to more than one county's projects appears under each. Use **County**, **Status** and
the search box above the list to narrow it further; they combine, so "Ada County" plus "Disabled"
finds only disabled Ada County contacts.

## Delete vs. Disable
**Disable** is reversible: it blocks sign-in but keeps the record, so **Enable** brings it straight
back. **Delete** is not — it permanently removes the contact, their sign-in and every session, and
can't be undone. Use Disable for "not right now"; use Delete only for a contact who should never
have existed here at all (added in error, or a person who's genuinely gone).

## Importing a county's user list
**Import from spreadsheet** brings in a whole roster at once instead of adding contacts one by one.
**Download template** for the expected columns (Name, Email, Project, plus optional Role/Office/
Department/Phone), fill it in, then **Choose file** and **Preview import** — nothing is written
until you review the preview and click **Confirm import**. Each row creates (or updates) a contact
and grants the named project, the same result as doing it by hand above.

Tick **Also add these people to the Project Contact List** to get a second use out of the same
spreadsheet: everyone imported is also added to their project's Project Contact List (the same
register the "Project Registers" page manages), so one import can seed both a county's portal
access and its contact directory. Leave it unticked to only set up portal access.
`,
    related: ['view-as-customer', 'roles-and-permissions', 'project-registers'],
  },
];

// ---------- Filtering, lookup, search (pure — exported for tests) ----------

// 'manager' covers anyone with something to manage: direct reports, a project they run, or forecast
// edit rights. Admins see everything. A viewer whose permissions failed to load sees only 'all'
// topics, which is the safe direction to fail.
export function canSee(topic, { isAdmin = false, permissions = null } = {}) {
  if (topic.audience === 'admin') return isAdmin;
  if (topic.audience === 'manager') {
    return (
      isAdmin ||
      !!(permissions &&
        (permissions.isManagerOfAnyone || permissions.isProjectManagerOfAny || permissions.canEditForecasts))
    );
  }
  return true;
}

// `flags` is { resources: bool, scheduling: bool } — a topic for a switched-off feature is noise, so
// it's filtered out of the list and out of search rather than shown as unavailable.
export function visibleTopics(viewer = {}, topics = HELP_TOPICS) {
  const flags = viewer.flags || {};
  return topics.filter((t) => canSee(t, viewer) && (!t.flag || flags[t.flag] !== false));
}

// Which topic answers "help with the screen I'm on". Longest matching route wins so a future
// '/my-time/detail' picks the more specific topic if one exists; '/' only ever matches exactly.
export function topicForPath(path, topics) {
  let best = null;
  for (const topic of topics) {
    for (const route of topic.routes || []) {
      const hit = route === '/' ? path === '/' : path === route || path.startsWith(`${route}/`);
      if (hit && (!best || route.length > best.route.length)) best = { topic, route };
    }
  }
  return best ? best.topic : null;
}

// Deliberately dumb substring scoring rather than a search library: the corpus is ~20 topics, and a
// title/keyword hit ranking above a body mention is the whole requirement.
export function searchTopics(topics, query) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const terms = q.split(/\s+/);
  return topics
    .map((topic) => {
      const title = topic.title.toLowerCase();
      const keywords = (topic.keywords || []).join(' ').toLowerCase();
      const summary = (topic.summary || '').toLowerCase();
      const body = (topic.body || '').toLowerCase();
      let score = 0;
      for (const term of terms) {
        if (title.includes(term)) score += 8;
        if (keywords.includes(term)) score += 6;
        if (summary.includes(term)) score += 3;
        if (body.includes(term)) score += 1;
      }
      // Every term has to land somewhere, otherwise "closed period" matches anything with "period".
      const allTermsHit = terms.every(
        (term) => title.includes(term) || keywords.includes(term) || summary.includes(term) || body.includes(term),
      );
      return allTermsHit ? { topic, score } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score || a.topic.title.localeCompare(b.topic.title))
    .map((r) => r.topic);
}

export function topicById(id, topics = HELP_TOPICS) {
  return topics.find((t) => t.id === id) || null;
}

// List order within a category: each screen-level topic followed by its `parent`-linked detail topics,
// so "Forecasting, in detail" reads as belonging to Resource Planning rather than competing with
// it. A child whose parent the viewer can't see is still listed, flat, rather than vanishing with it.
export function orderTopics(topics) {
  const present = new Set(topics.map((t) => t.id));
  const out = [];
  for (const topic of topics) {
    if (topic.parent && present.has(topic.parent)) continue;
    out.push({ topic, isChild: false });
    for (const child of topics) {
      if (child.parent === topic.id) out.push({ topic: child, isChild: true });
    }
  }
  return out;
}
